import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Package, ShoppingCart, Store, LogOut, Edit2, Power, PowerOff, Trash2, MapPin, CalendarDays, DollarSign, BarChart3, Download, Ticket, FileText } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from "@/hooks/use-toast";
import { useLocation, useNavigate } from 'react-router-dom';
import StoreForm from '@/components/StoreForm';
import { getMyStore, createStore, updateStore, toggleStoreStatus } from '@/services/stores';
import { getProductsByStoreId, createProduct, updateProduct, deleteProduct } from '@/services/products';
import { settings } from '@/services/apiClient';
import {
  getMerchantFinanceOverview,
  getMerchantPayoutAccount,
  updateMerchantPayoutAccount,
  verifyMerchantPayoutPassword,
  getMerchantFinanceReport,
  getMerchantVoucherCommissions,
  getMerchantVoucherSettings,
  upsertMerchantVoucherSettings,
  getMerchantTaxProfile,
  upsertMerchantTaxProfile,
} from '@/services/merchant';
import { resolveStoreLogo, resolveStoreBanner } from '@/lib/storeImages';
import { normalizeExtraLabel } from '../lib/extras';
import '../App.css';
import './Logista.css';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const ADDITIONALS_LABEL = 'Adicionais';
const LOGISTA_ALLOWED_TABS = new Set(['loja', 'produtos', 'financeiro', 'fiscal', 'voucher', 'metrica']);
const LOGISTA_ALLOWED_STATUS = new Set(['all', 'pending', 'completed', 'cancelled']);
const LOGISTA_ALLOWED_PAYMENTS = new Set(['all', 'cash', 'pix', 'card', 'voucher']);
const LOGISTA_PAYMENT_LABELS = {
  credit_card: 'Cartao de credito',
  debit_card: 'Cartao de debito',
  card: 'Cartao',
  pix: 'PIX',
  cash: 'Dinheiro',
  voucher: 'Voucher',
};
const VOUCHER_ISSUER_OPTIONS = [
  { value: 'ticket', label: 'Ticket' },
  { value: 'pluxee', label: 'Pluxee' },
  { value: 'biq', label: 'Biq' },
];
const VOUCHER_TYPE_OPTIONS = [
  { value: 'alimentacao', label: 'Alimentacao (VA)' },
  { value: 'refeicao', label: 'Refeicao (VR)' },
];
const PRODUCT_LIST_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Ativos' },
  { value: 'inactive', label: 'Inativos' },
  { value: 'promo', label: 'Em promocao' },
];
const LOGISTA_STORE_POLL_VISIBLE_MS = 60000;
const LOGISTA_STORE_POLL_HIDDEN_MS = 180000;

function isDocumentVisible() {
  if (typeof document === 'undefined') return true;
  return document.visibilityState === 'visible';
}

function formatDateInputValue(dateObj) {
  const year = String(dateObj.getFullYear());
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isValidDateInputValue(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || '').trim());
}

function resolvePeriodDateRange(periodRaw) {
  const period = String(periodRaw || '').trim().toLowerCase();
  if (period !== 'today' && period !== 'month') {
    return { date_from: '', date_to: '' };
  }
  const now = new Date();
  const today = formatDateInputValue(now);
  if (period === 'today') {
    return { date_from: today, date_to: today };
  }
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  return { date_from: formatDateInputValue(monthStart), date_to: today };
}

function formatStoreAddress(address) {
  if (!address || typeof address !== 'string') return 'Nao informado';
  const parts = address.split('|').map((item) => item.trim()).filter(Boolean);
  if (parts.length === 0) return 'Nao informado';
  if (parts.length !== 6) return address;
  const [rua, bairro, numero, cep, cidade, referencia] = parts;
  return `Rua ${rua}, ${numero} - ${bairro}, ${cidade}${cep ? `, CEP ${cep}` : ''}${referencia ? ` (${referencia})` : ''}`;
}




function formatPaymentMethodLabel(value) {
  const key = String(value || '').trim().toLowerCase();
  return LOGISTA_PAYMENT_LABELS[key] || value || '-';
}

function formatVoucherIssuerLabel(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'ticket') return 'Ticket';
  if (key === 'pluxee') return 'Pluxee';
  if (key === 'biq') return 'Biq';
  if (key === 'unknown') return 'Desconhecido';
  return value || '-';
}

function formatVoucherTypeLabel(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'alimentacao') return 'Alimentacao';
  if (key === 'refeicao') return 'Refeicao';
  if (key === 'unknown') return 'Desconhecido';
  return value || '-';
}

function formatVoucherCommissionStatus(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'pending') return 'Pendente';
  if (key === 'invoiced') return 'Faturada';
  if (key === 'paid') return 'Paga';
  if (key === 'overdue') return 'Vencida';
  return value || '-';
}

function formatVoucherApprovalStatus(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'approved') return 'Aprovado';
  if (key === 'rejected') return 'Rejeitado';
  return 'Pendente';
}

function formatTaxProfileStatus(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'approved') return 'Aprovado';
  if (key === 'rejected') return 'Rejeitado';
  return 'Pendente';
}

function normalizeSearchToken(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export default function Logista() {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState('loja');
  const [store, setStore] = useState(null);
  const [loadingStore, setLoadingStore] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    image_url: '',
    available: true,
    extras: [],
    promotion_enabled: false,
    promotion_discount_percent: '',
    admin_coupon_eligible: true,
  });
  const [productImageData, setProductImageData] = useState('');
  const [editingProductId, setEditingProductId] = useState(null);
  const [showProductCreateForm, setShowProductCreateForm] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productListFilter, setProductListFilter] = useState('all');
  const [commerceSettings, setCommerceSettings] = useState({
    admin_fee_percent: 10,
    admin_coupon: {
      enabled: false,
      code: 'CORREFOOD',
      discount_percent: 0,
      apply_on_promotional_products: true,
    }
  });
  const [financeFilters, setFinanceFilters] = useState({
    date_from: '',
    date_to: '',
    status: 'all',
    payment: 'all',
    q: '',
  });
  const [financeData, setFinanceData] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeError, setFinanceError] = useState('');
  const [voucherCommissionsData, setVoucherCommissionsData] = useState(null);
  const [voucherCommissionsLoading, setVoucherCommissionsLoading] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    method: 'pix',
    pix_key: '',
    bank_name: '',
    bank_agency: '',
    bank_account: '',
    bank_account_type: '',
    bank_holder_name: '',
    bank_holder_document: '',
  });
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [savedPayoutSnapshot, setSavedPayoutSnapshot] = useState(null);
  const [isPayoutEditing, setIsPayoutEditing] = useState(true);
  const [showPayoutUnlockCard, setShowPayoutUnlockCard] = useState(false);
  const [payoutUnlockPassword, setPayoutUnlockPassword] = useState('');
  const [payoutUnlocking, setPayoutUnlocking] = useState(false);
  const [acceptsCashFromCustomer, setAcceptsCashFromCustomer] = useState(true);
  const [cashPaymentSettingSaving, setCashPaymentSettingSaving] = useState(false);
  const [savedCashPaymentSetting, setSavedCashPaymentSetting] = useState(null);
  const [isCashPaymentEditing, setIsCashPaymentEditing] = useState(true);
  const cashSettingInitializedForStoreRef = useRef('');
  const [reportLoading, setReportLoading] = useState('');
  const [voucherSettingsLoading, setVoucherSettingsLoading] = useState(false);
  const [voucherSettingsSaving, setVoucherSettingsSaving] = useState(false);
  const [voucherSettingsError, setVoucherSettingsError] = useState('');
  const [taxProfileLoading, setTaxProfileLoading] = useState(false);
  const [taxProfileSaving, setTaxProfileSaving] = useState(false);
  const [taxProfileError, setTaxProfileError] = useState('');
  const [voucherPrerequisites, setVoucherPrerequisites] = useState({
    has_tax_profile: false,
    tax_profile_complete: false,
    tax_profile_approved: false,
    tax_profile_status: 'pending',
  });
  const [voucherSettingsForm, setVoucherSettingsForm] = useState({
    accepts_voucher: false,
    accepted_issuers: [],
    accepted_types: [],
    partner_store_id: '',
    approval_status: 'pending',
    enabled_for_checkout: false,
    rejection_reason: null,
    reviewed_at: null,
    approved_at: null,
  });
  const [taxProfileForm, setTaxProfileForm] = useState({
    person_type: 'pj',
    document_type: 'cnpj',
    document_number: '',
    legal_name: '',
    trade_name: '',
    cnae_primary: '',
    status: 'pending',
    rejection_reason: null,
    reviewed_at: null,
    is_complete: false,
  });

  const storeLogo = resolveStoreLogo(store);
  const storeBanner = resolveStoreBanner(store);
  const storeCategoryList = useMemo(() => {
    const normalized = [];
    const seen = new Set();

    const pushCategory = (value) => {
      const token = String(value || '').trim();
      if (!token) return;
      const key = token.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      normalized.push(token);
    };

    if (Array.isArray(store?.categories)) {
      store.categories.forEach(pushCategory);
    }

    const legacyCategoryRaw = String(store?.category || '').trim();
    if (legacyCategoryRaw) {
      legacyCategoryRaw.split(',').forEach(pushCategory);
    }

    return normalized.slice(0, 6);
  }, [store]);
  const hasStoreCoordinates = store?.lat != null && store?.lng != null;

  useEffect(() => {
    const storeId = String(store?.id || '').trim();
    if (!storeId) {
      cashSettingInitializedForStoreRef.current = '';
      setSavedCashPaymentSetting(null);
      setIsCashPaymentEditing(true);
      setAcceptsCashFromCustomer(true);
      return;
    }

    const serverValue = store?.accepts_cash !== false;
    const isNewStore = cashSettingInitializedForStoreRef.current !== storeId;
    if (isNewStore) {
      cashSettingInitializedForStoreRef.current = storeId;
      setAcceptsCashFromCustomer(serverValue);
      setSavedCashPaymentSetting(serverValue);
      setIsCashPaymentEditing(false);
      return;
    }

    setSavedCashPaymentSetting(serverValue);
    if (!isCashPaymentEditing) {
      setAcceptsCashFromCustomer(serverValue);
    }
  }, [store?.id, store?.accepts_cash, isCashPaymentEditing]);

  useEffect(() => {
    let isMounted = true;

    async function init() {
      if (!isMounted) return;

      if (!user) {
        setLoadingStore(false);
        return;
      }

      await loadStore(isMounted);
    }

    init();

    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;

    let stopped = false;
    let inFlight = false;
    let timeoutId = null;

    const schedule = () => {
      if (stopped) return;
      const delay = isDocumentVisible() ? LOGISTA_STORE_POLL_VISIBLE_MS : LOGISTA_STORE_POLL_HIDDEN_MS;
      timeoutId = window.setTimeout(tick, delay);
    };

    const tick = async () => {
      if (stopped) return;
      if (!isDocumentVisible() || inFlight) {
        schedule();
        return;
      }

      inFlight = true;
      try {
        const data = await getMyStore();
        if (!stopped && data) setStore(data);
      } catch (_) {
        // Atualizacao silenciosa do status da loja.
      } finally {
        inFlight = false;
        schedule();
      }
    };

    schedule();

    return () => {
      stopped = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const tabParam = String(params.get('tab') || '').trim().toLowerCase();
    if (LOGISTA_ALLOWED_TABS.has(tabParam)) {
      setActive(tabParam);
    }

    const hasFilterParams = ['period', 'date_from', 'date_to', 'status', 'payment', 'q'].some((key) => params.has(key));
    if (!hasFilterParams) return;

    const periodRange = resolvePeriodDateRange(params.get('period'));
    const dateFromRaw = String(params.get('date_from') || '').trim();
    const dateToRaw = String(params.get('date_to') || '').trim();
    const statusRaw = String(params.get('status') || 'all').trim().toLowerCase();
    const paymentRaw = String(params.get('payment') || 'all').trim().toLowerCase();

    let dateFrom = periodRange.date_from;
    let dateTo = periodRange.date_to;
    if (isValidDateInputValue(dateFromRaw)) dateFrom = dateFromRaw;
    if (isValidDateInputValue(dateToRaw)) dateTo = dateToRaw;

    setFinanceFilters({
      date_from: dateFrom,
      date_to: dateTo,
      status: LOGISTA_ALLOWED_STATUS.has(statusRaw) ? statusRaw : 'all',
      payment: LOGISTA_ALLOWED_PAYMENTS.has(paymentRaw) ? paymentRaw : 'all',
      q: String(params.get('q') || '').trim(),
    });
  }, [location.search]);

  useEffect(() => {
    let mounted = true;

    async function loadCommerceSettings() {
      try {
        const payload = await settings.getPublicCommerceSettings();
        if (!mounted || !payload || typeof payload !== 'object') return;
        setCommerceSettings({
          admin_fee_percent: Number(payload.admin_fee_percent || 10),
          admin_coupon: {
            enabled: Boolean(payload.admin_coupon?.enabled),
            code: String(payload.admin_coupon?.code || 'CORREFOOD'),
            discount_percent: Number(payload.admin_coupon?.discount_percent || 0),
            apply_on_promotional_products: payload.admin_coupon?.apply_on_promotional_products !== false,
          },
        });
      } catch (_) {
        // fallback silencioso com valores padrao
      }
    }

    loadCommerceSettings();
    return () => {
      mounted = false;
    };
  }, []);

  async function loadStore(isMounted = true) {
    if (!isMounted) return;

    setLoadingStore(true);
    try {
      const data = await getMyStore();

      if (!isMounted) return;

      setStore(data);
      if (!data) {
        setShowForm(true);
      }
    } catch (err) {
      if (isMounted) {
        console.error('Erro ao carregar loja:', err);
      }
    } finally {
      if (isMounted) {
        setLoadingStore(false);
      }
    }
  }

  useEffect(() => {
    if (!store?.id) {
      setProducts([]);
      return;
    }

    loadProducts(store.id);
  }, [store?.id]);

  useEffect(() => {
    if (active !== 'produtos') return;
    setShowProductCreateForm(false);
    resetProductForm();
  }, [active]);

  async function loadProducts(storeId) {
    setLoadingProducts(true);
    try {
      const data = await getProductsByStoreId(storeId);
      const normalizedProducts = (Array.isArray(data) ? data : []).map((product) => {
        const extras = normalizeExtrasFromProduct(product);
        return {
          ...product,
          extras,
          adicionais: extras,
        };
      });
      setProducts(normalizedProducts);
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }

  function mapPayoutAccountToForm(account) {
    const source = account && typeof account === 'object' ? account : {};
    const method = String(source.method || 'pix').toLowerCase() === 'bank' ? 'bank' : 'pix';
    return {
      method,
      pix_key: source.pix_key || '',
      bank_name: source.bank_name || '',
      bank_agency: source.bank_agency || '',
      bank_account: source.bank_account || '',
      bank_account_type: source.bank_account_type || '',
      bank_holder_name: source.bank_holder_name || '',
      bank_holder_document: source.bank_holder_document || '',
    };
  }

  function hasConfiguredPayoutData(data) {
    const source = data && typeof data === 'object' ? data : {};
    const method = String(source.method || 'pix').trim().toLowerCase();
    if (method === 'bank') {
      return [
        source.bank_name,
        source.bank_agency,
        source.bank_account,
        source.bank_account_type,
        source.bank_holder_name,
        source.bank_holder_document,
      ].some((value) => String(value || '').trim().length > 0);
    }
    return String(source.pix_key || '').trim().length > 0;
  }

  function normalizePayoutPayloadFromForm(data) {
    const source = data && typeof data === 'object' ? data : {};
    return {
      method: String(source.method || 'pix').trim().toLowerCase() === 'bank' ? 'bank' : 'pix',
      pix_key: String(source.pix_key || '').trim() || null,
      bank_name: String(source.bank_name || '').trim() || null,
      bank_agency: String(source.bank_agency || '').trim() || null,
      bank_account: String(source.bank_account || '').trim() || null,
      bank_account_type: String(source.bank_account_type || '').trim() || null,
      bank_holder_name: String(source.bank_holder_name || '').trim() || null,
      bank_holder_document: String(source.bank_holder_document || '').trim() || null,
    };
  }

  function arePayoutPayloadsEqual(left, right) {
    const leftData = normalizePayoutPayloadFromForm(left);
    const rightData = normalizePayoutPayloadFromForm(right);
    return (
      leftData.method === rightData.method
      && leftData.pix_key === rightData.pix_key
      && leftData.bank_name === rightData.bank_name
      && leftData.bank_agency === rightData.bank_agency
      && leftData.bank_account === rightData.bank_account
      && leftData.bank_account_type === rightData.bank_account_type
      && leftData.bank_holder_name === rightData.bank_holder_name
      && leftData.bank_holder_document === rightData.bank_holder_document
    );
  }

  function normalizeVoucherToken(value) {
    return String(value || '').trim().toLowerCase();
  }

  function normalizeDocumentDigits(value) {
    return String(value || '').replace(/\D/g, '');
  }

  function mapVoucherSettingsToForm(voucher) {
    const source = voucher && typeof voucher === 'object' ? voucher : {};
    const acceptedIssuers = Array.isArray(source.accepted_issuers)
      ? source.accepted_issuers.map((item) => normalizeVoucherToken(item)).filter(Boolean)
      : [];
    const acceptedTypes = Array.isArray(source.accepted_types)
      ? source.accepted_types.map((item) => normalizeVoucherToken(item)).filter(Boolean)
      : [];
    return {
      accepts_voucher: Boolean(source.accepts_voucher),
      accepted_issuers: [...new Set(acceptedIssuers)],
      accepted_types: [...new Set(acceptedTypes)],
      partner_store_id: String(source.partner_store_id || '').trim(),
      approval_status: String(source.approval_status || 'pending').trim().toLowerCase() || 'pending',
      enabled_for_checkout: source.enabled_for_checkout === true,
      rejection_reason: source.rejection_reason || null,
      reviewed_at: source.reviewed_at || null,
      approved_at: source.approved_at || null,
    };
  }

  function mapTaxProfileToForm(taxProfile) {
    const source = taxProfile && typeof taxProfile === 'object' ? taxProfile : {};
    const personType = String(source.person_type || '').trim().toLowerCase() === 'pf' ? 'pf' : 'pj';
    return {
      person_type: personType,
      document_type: String(source.document_type || (personType === 'pf' ? 'cpf' : 'cnpj')).trim().toLowerCase(),
      document_number: String(source.document_number || '').trim(),
      legal_name: String(source.legal_name || '').trim(),
      trade_name: String(source.trade_name || '').trim(),
      cnae_primary: String(source.cnae_primary || '').trim(),
      status: String(source.status || 'pending').trim().toLowerCase() || 'pending',
      rejection_reason: source.rejection_reason || null,
      reviewed_at: source.reviewed_at || null,
      is_complete: source.is_complete === true,
    };
  }

  async function loadFinanceData(currentFilters = financeFilters, options = {}) {
    if (!store?.id) return;
    const forceVoucher = options?.forceVoucher === true;
    const paymentFilter = forceVoucher ? 'voucher' : (currentFilters.payment || 'all');
    setFinanceLoading(true);
    setVoucherCommissionsLoading(true);
    setFinanceError('');
    try {
      const [overview, payout, voucherCommissions] = await Promise.all([
        getMerchantFinanceOverview({
          date_from: currentFilters.date_from || undefined,
          date_to: currentFilters.date_to || undefined,
          status: currentFilters.status || 'all',
          payment: paymentFilter,
          q: currentFilters.q || undefined,
          limit: 500,
        }),
        getMerchantPayoutAccount(),
        getMerchantVoucherCommissions({ status: 'all', limit: 500 }).catch(() => null),
      ]);
      setFinanceData(overview);
      const payoutMapped = mapPayoutAccountToForm(payout?.payout_account);
      const hasSavedPayout = hasConfiguredPayoutData(payoutMapped);
      setPayoutForm(payoutMapped);
      setSavedPayoutSnapshot(payoutMapped);
      setIsPayoutEditing(!hasSavedPayout);
      setShowPayoutUnlockCard(false);
      setPayoutUnlockPassword('');
      if (voucherCommissions && typeof voucherCommissions === 'object') {
        setVoucherCommissionsData(voucherCommissions);
      } else {
        setVoucherCommissionsData({
          summary: overview?.voucher_commissions?.summary || null,
          commissions: [],
        });
      }
    } catch (err) {
      setFinanceError(err?.message || 'Nao foi possivel carregar os dados financeiros.');
      setVoucherCommissionsData(null);
    } finally {
      setFinanceLoading(false);
      setVoucherCommissionsLoading(false);
    }
  }

  async function loadVoucherSettings() {
    if (!store?.id) return;
    setVoucherSettingsLoading(true);
    setVoucherSettingsError('');
    try {
      const payload = await getMerchantVoucherSettings();
      setVoucherSettingsForm(mapVoucherSettingsToForm(payload?.voucher));
      if (payload?.tax_profile) {
        setTaxProfileForm(mapTaxProfileToForm(payload.tax_profile));
      }
      const prerequisites = payload?.voucher_prerequisites && typeof payload.voucher_prerequisites === 'object'
        ? payload.voucher_prerequisites
        : {};
      setVoucherPrerequisites({
        has_tax_profile: prerequisites.has_tax_profile === true,
        tax_profile_complete: prerequisites.tax_profile_complete === true,
        tax_profile_approved: prerequisites.tax_profile_approved === true,
        tax_profile_status: String(prerequisites.tax_profile_status || 'pending').trim().toLowerCase() || 'pending',
      });
    } catch (err) {
      setVoucherSettingsError(err?.message || 'Nao foi possivel carregar configuracao de voucher.');
    } finally {
      setVoucherSettingsLoading(false);
    }
  }

  async function loadTaxProfile() {
    if (!store?.id) return;
    setTaxProfileLoading(true);
    setTaxProfileError('');
    try {
      const payload = await getMerchantTaxProfile();
      const mapped = mapTaxProfileToForm(payload?.tax_profile);
      setTaxProfileForm(mapped);
      setVoucherPrerequisites((prev) => ({
        ...prev,
        has_tax_profile: Boolean(payload?.tax_profile),
        tax_profile_complete: mapped.is_complete === true,
        tax_profile_approved: mapped.status === 'approved',
        tax_profile_status: mapped.status || prev.tax_profile_status || 'pending',
      }));
    } catch (err) {
      setTaxProfileError(err?.message || 'Nao foi possivel carregar perfil fiscal.');
    } finally {
      setTaxProfileLoading(false);
    }
  }

  useEffect(() => {
    if (!store?.id) return;
    if (active !== 'financeiro' && active !== 'metrica' && active !== 'voucher') return;
    loadFinanceData(financeFilters, { forceVoucher: active === 'voucher' });
  }, [active, store?.id]);

  useEffect(() => {
    if (!store?.id) return;
    if (active !== 'voucher') return;
    loadVoucherSettings();
  }, [active, store?.id]);

  useEffect(() => {
    if (!store?.id) return;
    if (active !== 'voucher' && active !== 'fiscal') return;
    loadTaxProfile();
  }, [active, store?.id]);

  useEffect(() => {
    if (active !== 'financeiro') return;
    if (financeFilters.payment !== 'voucher') return;
    const nextFilters = { ...financeFilters, payment: 'all' };
    setFinanceFilters(nextFilters);
    loadFinanceData(nextFilters);
  }, [active, financeFilters.payment]);

  async function handleApplyFinanceFilters() {
    await loadFinanceData(financeFilters);
  }

  async function handleApplyVoucherFilters() {
    await loadFinanceData(financeFilters, { forceVoucher: true });
  }

  function handleVoucherSettingChange(field, value) {
    setVoucherSettingsForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleVoucherListToggle(field, value, checked) {
    const token = normalizeVoucherToken(value);
    if (!token) return;
    setVoucherSettingsForm((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const hasItem = current.includes(token);
      let next = current;
      if (checked && !hasItem) {
        next = [...current, token];
      } else if (!checked && hasItem) {
        next = current.filter((item) => item !== token);
      }
      return { ...prev, [field]: next };
    });
  }

  async function handleSaveVoucherSettings() {
    setVoucherSettingsSaving(true);
    setVoucherSettingsError('');
    try {
      const payload = {
        accepts_voucher: Boolean(voucherSettingsForm.accepts_voucher),
        accepted_issuers: voucherSettingsForm.accepts_voucher
          ? (Array.isArray(voucherSettingsForm.accepted_issuers) ? voucherSettingsForm.accepted_issuers : [])
          : [],
        accepted_types: voucherSettingsForm.accepts_voucher
          ? (Array.isArray(voucherSettingsForm.accepted_types) ? voucherSettingsForm.accepted_types : [])
          : [],
        partner_store_id: voucherSettingsForm.accepts_voucher
          ? String(voucherSettingsForm.partner_store_id || '').trim() || null
          : null,
      };
      const response = await upsertMerchantVoucherSettings(payload);
      setVoucherSettingsForm(mapVoucherSettingsToForm(response?.voucher));
      await loadVoucherSettings();
      toast({
        title: 'Configuracao salva',
        description: 'Configuracao de voucher atualizada. Aguarde revisao do administrador.',
      });
    } catch (err) {
      setVoucherSettingsError(err?.message || 'Nao foi possivel salvar configuracao de voucher.');
      toast({
        variant: 'destructive',
        title: 'Erro ao salvar voucher',
        description: err?.message || 'Nao foi possivel salvar configuracao de voucher.',
      });
    } finally {
      setVoucherSettingsSaving(false);
    }
  }

  function handleTaxProfileChange(field, value) {
    setTaxProfileForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'person_type') {
        const personType = value === 'pf' ? 'pf' : 'pj';
        next.person_type = personType;
        next.document_type = personType === 'pf' ? 'cpf' : 'cnpj';
        if (personType === 'pf') {
          next.cnae_primary = '';
        }
      }
      return next;
    });
  }

  async function handleSaveTaxProfile() {
    setTaxProfileSaving(true);
    setTaxProfileError('');
    try {
      const payload = {
        person_type: taxProfileForm.person_type === 'pf' ? 'pf' : 'pj',
        document_number: normalizeDocumentDigits(taxProfileForm.document_number),
        legal_name: String(taxProfileForm.legal_name || '').trim() || null,
        trade_name: String(taxProfileForm.trade_name || '').trim() || null,
        cnae_primary: taxProfileForm.person_type === 'pj'
          ? String(taxProfileForm.cnae_primary || '').trim() || null
          : null,
      };
      const response = await upsertMerchantTaxProfile(payload);
      const mapped = mapTaxProfileToForm(response?.tax_profile);
      setTaxProfileForm(mapped);
      setVoucherPrerequisites((prev) => ({
        ...prev,
        has_tax_profile: true,
        tax_profile_complete: mapped.is_complete === true,
        tax_profile_approved: mapped.status === 'approved',
        tax_profile_status: mapped.status || prev.tax_profile_status || 'pending',
      }));
      await loadVoucherSettings();
      toast({
        title: 'Perfil fiscal salvo',
        description: 'Dados fiscais atualizados e enviados para revisao do administrador.',
      });
    } catch (err) {
      setTaxProfileError(err?.message || 'Nao foi possivel salvar perfil fiscal.');
      toast({
        variant: 'destructive',
        title: 'Erro no perfil fiscal',
        description: err?.message || 'Nao foi possivel salvar perfil fiscal.',
      });
    } finally {
      setTaxProfileSaving(false);
    }
  }

  async function handleSavePayoutAccount() {
    const normalizedPayload = normalizePayoutPayloadFromForm(payoutForm);
    const hadSavedPayout = hasConfiguredPayoutData(savedPayoutSnapshot);
    const payoutChanged = !arePayoutPayloadsEqual(savedPayoutSnapshot, normalizedPayload);

    if (hadSavedPayout && !isPayoutEditing) {
      return;
    }
    if (hadSavedPayout && !payoutChanged) {
      setIsPayoutEditing(false);
      setShowPayoutUnlockCard(false);
      setPayoutUnlockPassword('');
      toast({
        title: 'Sem alteracoes',
        description: 'Os dados de repasse ja estao salvos.',
      });
      return;
    }
    if (hadSavedPayout && payoutChanged && !payoutUnlockPassword.trim()) {
      toast({
        variant: 'destructive',
        title: 'Confirmacao obrigatoria',
        description: 'Confirme sua senha para alterar os dados de recebimento.',
      });
      setShowPayoutUnlockCard(true);
      return;
    }

    setPayoutSaving(true);
    try {
      const payload = {
        ...normalizedPayload,
        password_confirmation: hadSavedPayout && payoutChanged ? payoutUnlockPassword : null,
      };
      const response = await updateMerchantPayoutAccount(payload);
      const nextPayoutForm = mapPayoutAccountToForm(response?.payout_account || normalizedPayload);
      setPayoutForm(nextPayoutForm);
      setSavedPayoutSnapshot(nextPayoutForm);
      setIsPayoutEditing(false);
      setShowPayoutUnlockCard(false);
      setPayoutUnlockPassword('');
      toast({
        title: 'Dados salvos',
        description: 'Conta de repasse atualizada com sucesso.',
      });
      await loadFinanceData(financeFilters);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro ao salvar',
        description: err?.message || 'Nao foi possivel salvar os dados de recebimento.',
      });
    } finally {
      setPayoutSaving(false);
    }
  }

  async function handleUnlockPayoutEditing() {
    const password = payoutUnlockPassword.trim();
    if (!password) {
      toast({
        variant: 'destructive',
        title: 'Senha obrigatoria',
        description: 'Informe sua senha para liberar a alteracao.',
      });
      return;
    }

    setPayoutUnlocking(true);
    try {
      await verifyMerchantPayoutPassword(password);
      setIsPayoutEditing(true);
      setShowPayoutUnlockCard(false);
      toast({
        title: 'Edicao liberada',
        description: 'Agora voce pode alterar os dados de recebimento.',
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Senha invalida',
        description: err?.message || 'Nao foi possivel validar sua senha.',
      });
    } finally {
      setPayoutUnlocking(false);
    }
  }

  async function handleSaveCashPaymentSetting() {
    if (!store?.id) return;
    setCashPaymentSettingSaving(true);
    try {
      const updatedStore = await updateStore(store.id, {
        accepts_cash: Boolean(acceptsCashFromCustomer),
      });
      const persistedValue = updatedStore?.accepts_cash !== false;
      setStore(updatedStore);
      setSavedCashPaymentSetting(persistedValue);
      setAcceptsCashFromCustomer(persistedValue);
      setIsCashPaymentEditing(false);
      toast({
        title: 'Configuracao salva',
        description: persistedValue
          ? 'Pagamento em dinheiro habilitado para esta loja.'
          : 'Pagamento em dinheiro desabilitado para esta loja.',
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro ao salvar',
        description: err?.message || 'Nao foi possivel salvar a configuracao de dinheiro.',
      });
    } finally {
      setCashPaymentSettingSaving(false);
    }
  }

  function handleUnlockCashPaymentSetting() {
    setIsCashPaymentEditing(true);
  }

  function escapeCsvCell(value) {
    const raw = String(value ?? '');
    if (raw.includes('"') || raw.includes(',') || raw.includes('\n')) {
      return `"${raw.replace(/"/g, '""')}"`;
    }
    return raw;
  }

  function buildCsvContent(headers, rows) {
    const lines = [];
    lines.push(headers.map(escapeCsvCell).join(','));
    rows.forEach((row) => {
      lines.push(row.map(escapeCsvCell).join(','));
    });
    return lines.join('\n');
  }

  function triggerCsvDownload(filename, csvContent) {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async function handleDownloadReport(period) {
    const normalizedPeriod = period === 'weekly' ? 'weekly' : 'monthly';
    setReportLoading(normalizedPeriod);
    try {
      const report = await getMerchantFinanceReport(normalizedPeriod);
      const summary = report?.summary || {};
      const rows = Array.isArray(report?.rows) ? report.rows : [];
      const metadataRows = [
        ['Loja', report?.store?.name || '-'],
        ['Periodo', normalizedPeriod === 'weekly' ? 'Semanal' : 'Mensal'],
        ['Data inicio', report?.date_from || '-'],
        ['Data fim', report?.date_to || '-'],
        ['Pedidos', summary.orders_filtered ?? 0],
        ['Vendas totais (R$)', summary.sales_total ?? 0],
        ['Ticket medio (R$)', summary.ticket_avg ?? 0],
        ['Taxa admin (R$)', summary.admin_fee_total ?? 0],
        ['Valor a receber (R$)', summary.value_to_receive ?? 0],
        ['Valor recebido (R$)', summary.value_received ?? 0],
        ['Divida cash (R$)', summary.cash_debt ?? 0],
        ['Gerado em', report?.generated_at || '-'],
      ];
      const detailHeaders = [
        'Pedido',
        'Status',
        'Pagamento',
        'Entrega',
        'Total loja (R$)',
        'Taxa admin (R$)',
        'Repasse (R$)',
        'Status repasse',
        'Divida cash (R$)',
        'Status divida cash',
        'Criado em',
      ];
      const detailRows = rows.map((row) => [
        row.pedido,
        row.status,
        formatPaymentMethodLabel(row.pagamento),
        row.entrega,
        row.total_loja,
        row.taxa_admin,
        row.repasse,
        row.status_repasse,
        row.divida_cash,
        row.status_divida_cash,
        row.criado_em,
      ]);

      const csvParts = [];
      csvParts.push(buildCsvContent(['Campo', 'Valor'], metadataRows));
      csvParts.push('');
      csvParts.push(buildCsvContent(detailHeaders, detailRows));
      const now = new Date();
      const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      triggerCsvDownload(`relatorio-${normalizedPeriod}-${stamp}.csv`, csvParts.join('\n'));
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro ao gerar relatorio',
        description: err?.message || 'Nao foi possivel gerar o relatorio.',
      });
    } finally {
      setReportLoading('');
    }
  }

  function handleProductInputChange(event) {
    const { name, value, type, checked } = event.target;
    setProductForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  function getExtraPriceInReais(extra) {
    if (!extra || typeof extra !== 'object') return 0;
    if (typeof extra.price === 'number' && Number.isFinite(extra.price)) return extra.price;
    if (typeof extra.preco === 'number' && Number.isFinite(extra.preco)) return extra.preco;
    if (typeof extra.price_cents === 'number' && Number.isFinite(extra.price_cents)) return extra.price_cents / 100;
    if (typeof extra.preco_centavos === 'number' && Number.isFinite(extra.preco_centavos)) return extra.preco_centavos / 100;
    return 0;
  }

  function normalizeExtrasFromProduct(product) {
    const extras = Array.isArray(product?.extras)
      ? product.extras
      : (Array.isArray(product?.adicionais)
        ? product.adicionais
        : (Array.isArray(product?.sitio) ? product.sitio : []));

    return extras
      .filter((extra) => extra && typeof extra === 'object')
      .map((extra) => ({
        id: String(extra.id || `extra-${Date.now()}-${Math.random().toString(16).slice(2)}`),
        name: normalizeExtraLabel(extra.name || extra.nome),
        price: getExtraPriceInReais(extra).toFixed(2),
        max_quantity: Number(extra.max_quantity || extra.maximo || 1),
        enabled: extra.enabled !== false,
      }))
      .filter((extra) => extra.name);
  }

  function addExtraField() {
    setProductForm((prev) => ({
      ...prev,
      extras: [
        ...(Array.isArray(prev.extras) ? prev.extras : []),
        {
          id: `extra-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          name: '',
          price: '0.00',
          max_quantity: 1,
          enabled: true,
        },
      ],
    }));
  }

  function removeExtraField(extraId) {
    setProductForm((prev) => ({
      ...prev,
      extras: (Array.isArray(prev.extras) ? prev.extras : []).filter((extra) => extra.id !== extraId),
    }));
  }

  function updateExtraField(extraId, field, value) {
    setProductForm((prev) => ({
      ...prev,
      extras: (Array.isArray(prev.extras) ? prev.extras : []).map((extra) => {
        if (extra.id !== extraId) return extra;
        return {
          ...extra,
          [field]: value,
        };
      }),
    }));
  }

  function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handleProductImageFileChange(event) {
    const file = event.target.files?.[0];
    if (!file) {
      setProductImageData('');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        variant: 'destructive',
        title: 'Arquivo invalido',
        description: 'Selecione uma imagem valida.'
      });
      event.target.value = '';
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      setProductImageData(String(dataUrl));
    } catch (_) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Nao foi possivel ler a imagem.'
      });
    }
  }

  function resetProductForm() {
    setProductForm({
      name: '',
      description: '',
      price: '',
      image_url: '',
      available: true,
      extras: [],
      promotion_enabled: false,
      promotion_discount_percent: '',
      admin_coupon_eligible: true,
    });
    setProductImageData('');
    setEditingProductId(null);
  }

  function handleOpenCreateProductForm() {
    resetProductForm();
    setShowProductCreateForm(true);
  }

  function handleCloseProductForm() {
    resetProductForm();
    setShowProductCreateForm(false);
  }

  function formatMoney(value) {
    const numeric = Number(value);
    const normalized = Number.isFinite(numeric) ? numeric : 0;
    return `R$ ${normalized.toFixed(2).replace('.', ',')}`;
  }

  function formatDateTime(value) {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleString('pt-BR');
  }

  function getChartMaxValue(items, key) {
    if (!Array.isArray(items) || items.length === 0) return 0;
    return items.reduce((max, item) => {
      const current = Number(item?.[key] || 0);
      return current > max ? current : max;
    }, 0);
  }

  function getProductPriceInReais(product) {
    if (typeof product?.preco === 'number') {
      return product.preco;
    }
    if (typeof product?.price === 'number') {
      return Number.isInteger(product.price) ? product.price / 100 : product.price;
    }
    return 0;
  }

  function getProductDisplayName(product) {
    return String(product?.name || product?.nome || 'Produto sem nome').trim();
  }

  function getProductDisplayDescription(product) {
    return String(product?.description || product?.descricao || '').trim();
  }

  function getPromotionFinalPriceInReais(product) {
    if (!product?.promotion?.is_active) return null;
    const direct = Number(product?.promotion?.final_price);
    if (Number.isFinite(direct) && direct >= 0) return direct;
    const cents = Number(product?.promotion?.final_price_cents);
    if (Number.isFinite(cents) && cents >= 0) return cents / 100;
    return null;
  }

  function parsePriceToNumber(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    const raw = String(value ?? '').trim().replace(/\s/g, '').replace('R$', '');
    if (!raw) return NaN;
    if (raw.includes(',') && raw.includes('.') && raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
      return Number(raw.replace(/\./g, '').replace(',', '.'));
    }
    return Number(raw.replace(',', '.'));
  }

  function getPromotionPercentValue() {
    const raw = Number(productForm.promotion_discount_percent);
    if (!Number.isFinite(raw)) return 0;
    return Math.max(0, Math.min(100, raw));
  }

  const simulation = (() => {
    const base = parsePriceToNumber(productForm.price);
    const baseValue = Number.isFinite(base) && base > 0 ? base : 0;
    const merchantDiscountPercent = productForm.promotion_enabled ? getPromotionPercentValue() : 0;
    const merchantPrice = Math.max(0, baseValue * (1 - merchantDiscountPercent / 100));
    const adminFeePercent = Number(commerceSettings?.admin_fee_percent || 0);
    const merchantNet = Math.max(0, merchantPrice * (1 - adminFeePercent / 100));

    const adminCouponEnabled = Boolean(
      commerceSettings?.admin_coupon?.enabled
      && commerceSettings?.admin_coupon?.apply_on_promotional_products !== false
      && productForm.promotion_enabled
      && productForm.admin_coupon_eligible
    );
    const adminCouponPercent = adminCouponEnabled ? Number(commerceSettings?.admin_coupon?.discount_percent || 0) : 0;
    const customerFinal = Math.max(0, merchantPrice * (1 - adminCouponPercent / 100));

    return {
      baseValue,
      merchantDiscountPercent,
      merchantPrice,
      adminFeePercent,
      merchantNet,
      adminCouponEnabled,
      adminCouponPercent,
      customerFinal,
    };
  })();

  function handleEditProduct(product) {
    const promotion = product?.promotion && typeof product.promotion === 'object'
      ? product.promotion
      : {};
    setShowProductCreateForm(true);
    setEditingProductId(product.id);
    setProductForm({
      name: product.name || product.nome || '',
      description: product.description || product.descricao || '',
      price: getProductPriceInReais(product).toFixed(2),
      image_url: product.image_url || product.imagem || '',
      available: product.available !== false,
      extras: normalizeExtrasFromProduct(product),
      promotion_enabled: Boolean(promotion.is_active),
      promotion_discount_percent: promotion.discount_percent != null ? String(promotion.discount_percent) : '',
      admin_coupon_eligible: promotion.admin_coupon_eligible !== false,
    });
    setProductImageData('');
  }

  async function handleDeleteProduct(productId) {
    if (!window.confirm('Deseja realmente excluir este produto?')) return;
    try {
      await deleteProduct(productId);
      await loadProducts(store.id);
      if (editingProductId === productId) {
        handleCloseProductForm();
      }
      toast({
        title: 'Produto excluido',
        description: 'O produto foi removido com sucesso.'
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro ao excluir',
        description: err.message || 'Nao foi possivel excluir o produto.'
      });
    }
  }

  async function handleCreateProduct(event) {
    event.preventDefault();

    if (!store?.id) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Cadastre uma loja antes de criar produtos.'
      });
      return;
    }

    const parsedPrice = parsePriceToNumber(productForm.price);

    if (!productForm.name.trim() || !Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      toast({
        variant: 'destructive',
        title: 'Campos obrigatorios',
        description: 'Informe nome e preco do produto.'
      });
      return;
    }

    setSavingProduct(true);
    try {
      const extrasPayload = (Array.isArray(productForm.extras) ? productForm.extras : [])
        .map((extra) => {
          const name = normalizeExtraLabel(extra.name);
          const parsedExtraPrice = parsePriceToNumber(extra.price);
          const maxQuantity = Number(extra.max_quantity || 1);
          return {
            id: extra.id,
            name,
            price: Number.isFinite(parsedExtraPrice) && parsedExtraPrice > 0 ? parsedExtraPrice : 0,
            max_quantity: Number.isFinite(maxQuantity) && maxQuantity > 0 ? Math.floor(maxQuantity) : 1,
            enabled: extra.enabled !== false,
          };
        })
        .filter((extra) => extra.name);

      const payload = {
        name: productForm.name.trim(),
        description: productForm.description?.trim() || null,
        price: parsedPrice,
        image_url: productImageData || productForm.image_url?.trim() || null,
        available: Boolean(productForm.available),
        extras: extrasPayload,
        promotion: {
          promotion_enabled: Boolean(productForm.promotion_enabled),
          promotion_discount_percent: getPromotionPercentValue(),
          admin_coupon_eligible: Boolean(productForm.admin_coupon_eligible),
        },
      };

      if (editingProductId) {
        await updateProduct(editingProductId, payload);
      } else {
        await createProduct({
          restaurant_id: store.id,
          ...payload
        });
      }

      resetProductForm();
      setShowProductCreateForm(false);
      await loadProducts(store.id);
      toast({
        title: 'Sucesso!',
        description: editingProductId ? 'Produto atualizado com sucesso.' : 'Produto cadastrado com sucesso.'
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: editingProductId ? 'Erro ao atualizar produto' : 'Erro ao cadastrar produto',
        description: err.message || 'Nao foi possivel salvar o produto.'
      });
    } finally {
      setSavingProduct(false);
    }
  }

  async function handleCreateStore(formData) {
    try {
      const newStore = await createStore(formData);
      setStore(newStore);
      await loadStore(true);
      setShowForm(false);
      toast({
        title: "Sucesso!",
        description: "Loja cadastrada com sucesso.",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: err.message || "Não foi possível cadastrar a loja.",
      });
      throw err;
    }
  }

  async function handleUpdateStore(formData) {
    try {
      const updatedStore = await updateStore(store.id, formData);
      setStore(updatedStore);
      await loadStore(true);
      setShowForm(false);
      toast({
        title: "Sucesso!",
        description: "Loja atualizada com sucesso.",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: err.message || "Não foi possível atualizar a loja.",
      });
      throw err;
    }
  }

  async function handleToggleStatus() {
    try {
      const newStatus = !store.is_open;
      const updatedStore = await toggleStoreStatus(store.id, newStatus);
      setStore(updatedStore);
      toast({
        title: newStatus ? "Loja aberta!" : "Loja fechada!",
        description: newStatus ? "Sua loja está recebendo pedidos." : "Sua loja não está aceitando novos pedidos.",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível alterar o status da loja.",
      });
    }
  }

  const isStoreOpen = Boolean(store?.is_open);
  const productSummary = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    return list.reduce((acc, product) => {
      const isActive = product?.available !== false;
      const hasPromo = Boolean(product?.promotion?.is_active);
      acc.total += 1;
      if (isActive) acc.active += 1;
      if (!isActive) acc.inactive += 1;
      if (hasPromo) acc.promo += 1;
      return acc;
    }, { total: 0, active: 0, inactive: 0, promo: 0 });
  }, [products]);

  const filteredProducts = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    const query = normalizeSearchToken(productSearchTerm);
    return list.filter((product) => {
      const isActive = product?.available !== false;
      const hasPromo = Boolean(product?.promotion?.is_active);

      if (productListFilter === 'active' && !isActive) return false;
      if (productListFilter === 'inactive' && isActive) return false;
      if (productListFilter === 'promo' && !hasPromo) return false;

      if (!query) return true;
      const searchableText = normalizeSearchToken([
        product?.name,
        product?.nome,
        product?.description,
        product?.descricao,
      ].filter(Boolean).join(' '));
      return searchableText.includes(query);
    });
  }, [products, productSearchTerm, productListFilter]);

  const hasSavedPayoutData = hasConfiguredPayoutData(savedPayoutSnapshot);
  const payoutFieldsLocked = hasSavedPayoutData && !isPayoutEditing;
  const financeSummary = financeData?.summary || {};
  const financeOrders = Array.isArray(financeData?.orders) ? financeData.orders : [];
  const financeTopProducts = Array.isArray(financeData?.top_products) ? financeData.top_products : [];
  const weekdaySales = Array.isArray(financeData?.charts?.weekday_sales) ? financeData.charts.weekday_sales : [];
  const monthdaySales = Array.isArray(financeData?.charts?.monthday_sales) ? financeData.charts.monthday_sales : [];
  const voucherCommissionsSummary = voucherCommissionsData?.summary || financeData?.voucher_commissions?.summary || {};
  const voucherCommissionsRows = Array.isArray(voucherCommissionsData?.commissions) ? voucherCommissionsData.commissions : [];
  const voucherOrders = financeOrders.filter((order) => normalizeVoucherToken(order?.payment_method) === 'voucher');
  const nonVoucherFinanceOrders = financeOrders.filter((order) => normalizeVoucherToken(order?.payment_method) !== 'voucher');
  const taxProfileReadyForVoucher = voucherPrerequisites.tax_profile_complete && voucherPrerequisites.tax_profile_approved;
  const taxDocumentDigits = normalizeDocumentDigits(taxProfileForm.document_number);
  const hasTaxDocumentNumber = taxProfileForm.person_type === 'pf' ? taxDocumentDigits.length === 11 : taxDocumentDigits.length === 14;
  const hasTaxLegalName = String(taxProfileForm.legal_name || '').trim().length >= 3;
  const hasTaxCnae = taxProfileForm.person_type === 'pf' || String(taxProfileForm.cnae_primary || '').trim().length >= 5;
  const voucherOrderNumberMap = financeOrders.reduce((acc, order) => {
    acc[String(order?.id || '')] = String(order?.order_number || '-');
    return acc;
  }, {});

  const resolveVoucherDetailsLabel = (order) => {
    const issuerRaw = order?.voucher_issuer || order?.voucher?.voucher_issuer || order?.voucher?.issuer;
    const typeRaw = order?.voucher_type || order?.voucher?.voucher_type || order?.voucher?.type;
    if (!issuerRaw && !typeRaw) return '-';
    return `${formatVoucherIssuerLabel(issuerRaw)} / ${formatVoucherTypeLabel(typeRaw)}`;
  };
  const weekdayMaxRevenue = getChartMaxValue(weekdaySales, 'revenue');
  const monthdayMaxRevenue = getChartMaxValue(monthdaySales, 'revenue');
  const cashSettingLocked = !isCashPaymentEditing;
  const hasCashSettingChanged =
    savedCashPaymentSetting === null
      ? true
      : Boolean(acceptsCashFromCustomer) !== Boolean(savedCashPaymentSetting);

  return (
    <div className="logista-page">
      <div className="perfil-container">
        <aside className="perfil-sidebar">
          <div className="sidebar-header">
            <h2 className="perfil-title">Painel do Logista</h2>
            {store && (
              <div className="store-status">
                <button
                  type="button"
                  className={`status-badge ${isStoreOpen ? 'open' : 'closed'}`}
                  onClick={handleToggleStatus}
                  title={isStoreOpen ? "Fechar loja" : "Abrir loja"}
                  aria-pressed={isStoreOpen}
                >
                  <span className={`status-icon status-icon-open ${isStoreOpen ? 'visible' : 'hidden'}`} aria-hidden={!isStoreOpen}>
                    <Power size={14} />
                  </span>
                  <span className={`status-icon status-icon-closed ${isStoreOpen ? 'hidden' : 'visible'}`} aria-hidden={isStoreOpen}>
                    <PowerOff size={14} />
                  </span>
                  <span className="status-text">{isStoreOpen ? 'Aberta' : 'Fechada'}</span>
                </button>
              </div>
            )}
          </div>

          <nav className="perfil-nav">
            <button
              className={`perfil-menu ${active === 'loja' ? 'active' : ''}`}
              onClick={() => setActive('loja')}
            >
              <Store size={18} />
              Minha Loja
            </button>

            <button
              className="perfil-menu"
              onClick={() => navigate('/merchant/dashboard')}
            >
              <ShoppingCart size={18} />
              Dashboard
            </button>

            <button
              className={`perfil-menu ${active === 'pedidos' ? 'active' : ''}`}
              onClick={() => navigate('/merchant/dashboard')}
              disabled={!store}
            >
              <ShoppingCart size={18} />
              Pedidos
            </button>

            <button
              className={`perfil-menu ${active === 'produtos' ? 'active' : ''}`}
              onClick={() => setActive('produtos')}
              disabled={!store}
            >
              <Package size={18} />
              Produtos
            </button>

            <button
              className={`perfil-menu ${active === 'financeiro' ? 'active' : ''}`}
              onClick={() => setActive('financeiro')}
              disabled={!store}
            >
              <DollarSign size={18} />
              Financeiro
            </button>

            <button
              className={`perfil-menu ${active === 'fiscal' ? 'active' : ''}`}
              onClick={() => setActive('fiscal')}
              disabled={!store}
            >
              <FileText size={18} />
              Configuracao fiscal
            </button>

            <button
              className={`perfil-menu ${active === 'voucher' ? 'active' : ''}`}
              onClick={() => setActive('voucher')}
              disabled={!store}
            >
              <Ticket size={18} />
              Voucher
            </button>

            <button
              className={`perfil-menu ${active === 'metrica' ? 'active' : ''}`}
              onClick={() => setActive('metrica')}
              disabled={!store}
            >
              <BarChart3 size={18} />
              Metrica
            </button>

            <button className="perfil-menu logout" onClick={signOut}>
              <LogOut size={18} />
              Sair
            </button>
          </nav>

        </aside>

        <main className="perfil-content">
          {active === 'loja' && (
            <div className="store-section">
              {loadingStore ? (
                <div className="loading-state">
                  <p>Carregando...</p>
                </div>
              ) : showForm ? (
                <StoreForm
                  initialData={store}
                  onSubmit={store ? handleUpdateStore : handleCreateStore}
                  onCancel={store ? () => setShowForm(false) : null}
                  isEditing={!!store}
                  userEmail={user?.email}
                />
              ) : store ? (
                <div className="store-display">
                  <div className="store-header">
                    <div className="store-header-info">
                      <h1 className="store-header-title">Minha Loja</h1>
                      <p className="store-header-subtitle">
                        Confira os dados principais da sua loja e mantenha tudo atualizado para vender melhor.
                      </p>
                      <div className="store-header-meta">
                        <span className={`store-meta-pill ${isStoreOpen ? 'open' : 'closed'}`}>
                          {isStoreOpen ? 'Loja aberta' : 'Loja fechada'}
                        </span>
                        <span className={`store-meta-pill ${hasStoreCoordinates ? 'ready' : 'warning'}`}>
                          {hasStoreCoordinates ? 'Localizacao configurada' : 'Localizacao pendente'}
                        </span>
                      </div>
                    </div>
                    <button className="btn-edit" onClick={() => setShowForm(true)}>
                      <Edit2 size={18} />
                      Editar
                    </button>
                  </div>

                  {!hasStoreCoordinates && (
                    <div className="store-location-warning">
                      Defina a localizacao da loja para liberar pedidos no raio do motoboy.
                    </div>
                  )}

                  <div className="store-info-grid">
                    {storeBanner && (
                      <div className="store-banner">
                        <img src={storeBanner} alt="Banner da loja" />
                      </div>
                    )}

                    <div className="store-basic-info">
                      {storeLogo && (
                        <img src={storeLogo} alt="Logo da loja" className="store-logo" />
                      )}
                      <div className="store-identity-content">
                        <h2>{store.name}</h2>
                        {storeCategoryList.length > 0 ? (
                          <div className="store-category-list">
                            {storeCategoryList.map((category) => (
                              <span key={category} className="store-category-chip">
                                {category}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="store-category">Categoria nao informada</p>
                        )}
                        {store.description && (
                          <p className="store-description">{store.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="store-details">
                      <div className="detail-item">
                        <p className="detail-label">Telefone</p>
                        <p className="detail-value">{store.phone || 'Nao informado'}</p>
                      </div>
                      <div className="detail-item">
                        <p className="detail-label with-icon">
                          <MapPin size={14} />
                          Endereco da loja
                        </p>
                        <p className="detail-value">{formatStoreAddress(store.address || store.endereco)}</p>
                      </div>
                      <div className="detail-item">
                        <p className="detail-label with-icon">
                          <CalendarDays size={14} />
                          Criada em
                        </p>
                        <p className="detail-value">
                          {store.created_at ? new Date(store.created_at).toLocaleDateString('pt-BR') : 'Nao informado'}
                        </p>
                      </div>
                      <div className="detail-item">
                        <p className="detail-label">Taxa de entrega</p>
                        <p className="detail-value">R$ {store.delivery_fee?.toFixed(2).replace('.', ',') || '0,00'}</p>
                      </div>
                      <div className="detail-item">
                        <p className="detail-label">Pedido minimo</p>
                        <p className="detail-value">R$ {store.min_order_value?.toFixed(2).replace('.', ',') || '0,00'}</p>
                      </div>
                    </div>

                    {store.opening_hours && (
                      <div className="opening-hours-display">
                        <h3>Horario de funcionamento</h3>
                        <div className="hours-list">
                          {Object.entries({
                            monday: 'Segunda',
                            tuesday: 'Terca',
                            wednesday: 'Quarta',
                            thursday: 'Quinta',
                            friday: 'Sexta',
                            saturday: 'Sabado',
                            sunday: 'Domingo'
                          }).map(([key, label]) => (
                            <div key={key} className="hour-item">
                              <span className="day">{label}</span>
                              <span className="time">
                                {store.opening_hours[key]?.closed ? (
                                  'Fechado'
                                ) : (
                                  `${store.opening_hours[key]?.open || '09:00'} - ${store.opening_hours[key]?.close || '22:00'}`
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {active === 'pedidos' && (
            <div className="p-6">
              <h1 className="text-2xl font-bold mb-4">
                Gerenciamento de Pedidos
              </h1>

              <Tabs defaultValue="pendentes" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="pendentes">Pendentes</TabsTrigger>
                  <TabsTrigger value="preparo">Em Preparo</TabsTrigger>
                  <TabsTrigger value="saiu">Saiu para Entrega</TabsTrigger>
                </TabsList>

                <TabsContent value="pendentes">
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ID</TableHead>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Itens</TableHead>
                          <TableHead>Total</TableHead>
                          <TableHead>Ação</TableHead>
                        </TableRow>
                      </TableHeader>

                      <TableBody>
                        <TableRow>
                          <TableCell className="text-center" colSpan={5}>
                            Nenhum pedido pendente
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>

                <TabsContent value="preparo">
                  <p>Pedidos em preparo...</p>
                </TabsContent>

                <TabsContent value="saiu">
                  <p>Pedidos enviados para entrega...</p>
                </TabsContent>
              </Tabs>
            </div>
          )}

          {active === 'produtos' && (
            <div className="p-6">
              <h1 className="text-2xl font-bold mb-4">Gestao de Produtos</h1>
              <p style={{ marginBottom: '12px', color: '#6b7280' }}>Cadastre, edite ou exclua produtos da sua loja.</p>

              {!showProductCreateForm ? (
                <div className="products-create-entry">
                  <button
                    type="button"
                    className="btn-primary products-create-entry-btn"
                    onClick={handleOpenCreateProductForm}
                    disabled={!store}
                  >
                    Cadastrar produto
                  </button>
                </div>
              ) : (
                <form onSubmit={handleCreateProduct} className="store-form" style={{ marginBottom: '24px' }}>
                  <div className="products-create-form-header">
                    <h2>{editingProductId ? 'Editar produto' : 'Novo produto'}</h2>
                    <p>Preencha os dados e finalize no botao ao final do formulario.</p>
                  </div>

                  <div className="form-group">
                    <label>Nome do produto</label>
                    <input
                      name="name"
                      value={productForm.name}
                      onChange={handleProductInputChange}
                      placeholder="Ex: X-Burger"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Descricao</label>
                    <textarea
                      name="description"
                      value={productForm.description}
                      onChange={handleProductInputChange}
                      placeholder="Descricao opcional"
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Imagem do produto (URL)</label>
                    <input
                      type="url"
                      name="image_url"
                      value={productForm.image_url}
                      onChange={handleProductInputChange}
                      placeholder="https://..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Ou anexe uma imagem</label>
                    <input
                      type="file"
                      name="image_file"
                      accept="image/*"
                      onChange={handleProductImageFileChange}
                    />
                    {(productImageData || productForm.image_url) ? (
                      <img
                        src={productImageData || productForm.image_url}
                        alt="Preview"
                        style={{ width: '88px', height: '88px', objectFit: 'cover', borderRadius: '8px', marginTop: '10px' }}
                      />
                    ) : null}
                  </div>

                  <div className="form-group">
                    <label>Preco (R$)</label>
                    <input
                      type="number"
                      name="price"
                      value={productForm.price}
                      onChange={handleProductInputChange}
                      placeholder="Ex: 29.90"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>

                  <div className="form-group product-availability-group">
                    <label>Disponibilidade do produto</label>
                    <p className="product-availability-hint">
                      Produtos inativos ficam ocultos para o cliente e podem ser reativados depois.
                    </p>
                    <div className="product-availability-toggle" role="group" aria-label="Disponibilidade">
                      <button
                        type="button"
                        className={`product-availability-option ${productForm.available ? 'active' : ''}`}
                        onClick={() => setProductForm((prev) => ({ ...prev, available: true }))}
                      >
                        Ativo
                      </button>
                      <button
                        type="button"
                        className={`product-availability-option ${!productForm.available ? 'inactive' : ''}`}
                        onClick={() => setProductForm((prev) => ({ ...prev, available: false }))}
                      >
                        Inativo
                      </button>
                    </div>
                  </div>

                  <div className="form-group promotion-group">
                    <label style={{ marginBottom: 8 }}>Promocao do produto</label>
                    <label className="promotion-check">
                      <input
                        type="checkbox"
                        name="promotion_enabled"
                        checked={Boolean(productForm.promotion_enabled)}
                        onChange={handleProductInputChange}
                      />
                      <span>Ativar desconto promocional neste produto</span>
                    </label>

                    {productForm.promotion_enabled ? (
                      <div className="promotion-controls">
                        <div className="form-group">
                          <label>Desconto do lojista (%)</label>
                          <input
                            type="number"
                            name="promotion_discount_percent"
                            min="0"
                            max="100"
                            step="0.1"
                            value={productForm.promotion_discount_percent}
                            onChange={handleProductInputChange}
                            placeholder="Ex: 20"
                          />
                        </div>

                        <label className="promotion-check">
                          <input
                            type="checkbox"
                            name="admin_coupon_eligible"
                            checked={Boolean(productForm.admin_coupon_eligible)}
                            onChange={handleProductInputChange}
                          />
                          <span>Permitir cupom admin neste produto promocional</span>
                        </label>

                        <div className="promotion-simulation">
                          <p><strong>Preco base:</strong> {formatMoney(simulation.baseValue)}</p>
                          <p><strong>Preco com desconto lojista:</strong> {formatMoney(simulation.merchantPrice)}</p>
                          <p><strong>Taxa admin ({simulation.adminFeePercent}%):</strong> {formatMoney(simulation.merchantPrice - simulation.merchantNet)}</p>
                          <p><strong>Liquido do lojista:</strong> {formatMoney(simulation.merchantNet)}</p>
                          {simulation.adminCouponEnabled ? (
                            <p>
                              <strong>Preco final cliente com cupom {commerceSettings?.admin_coupon?.code} ({simulation.adminCouponPercent}%):</strong>{' '}
                              {formatMoney(simulation.customerFinal)}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <label style={{ marginBottom: 0 }}>Adicionais opcionais</label>
                      <button type="button" className="btn-secondary extra-options-btn" onClick={addExtraField}>
                        Disponibilizar opcionais dentro do produto
                      </button>
                    </div>

                    {(Array.isArray(productForm.extras) ? productForm.extras : []).length === 0 ? (
                      <p style={{ margin: 0, color: '#6b7280', fontSize: '13px' }}>
                        Sem adicionais cadastrados para este produto.
                      </p>
                    ) : (
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {productForm.extras.map((extra) => (
                          <div
                            key={extra.id}
                            style={{
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              padding: '10px',
                              display: 'grid',
                              gridTemplateColumns: '2fr 1fr 1fr auto',
                              gap: '8px',
                              alignItems: 'center',
                            }}
                          >
                            <input
                              type="text"
                              placeholder="Nome do adicional (ex.: Catupiry)"
                              value={extra.name}
                              onChange={(event) => updateExtraField(extra.id, 'name', event.target.value)}
                            />
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Preco"
                              value={extra.price}
                              onChange={(event) => updateExtraField(extra.id, 'price', event.target.value)}
                            />
                            <input
                              type="number"
                              min="1"
                              max="20"
                              placeholder="Max"
                              value={extra.max_quantity}
                              onChange={(event) => updateExtraField(extra.id, 'max_quantity', event.target.value)}
                            />
                            <button
                              type="button"
                              className="product-action-btn delete"
                              onClick={() => removeExtraField(extra.id)}
                              title="Remover adicional"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="products-create-form-actions">
                    <button type="submit" className="btn-primary" disabled={savingProduct || !store}>
                      {savingProduct ? 'Salvando...' : (editingProductId ? 'Salvar Edicao' : 'Cadastrar Produto')}
                    </button>
                    <button type="button" className="btn-secondary" onClick={handleCloseProductForm}>
                      {editingProductId ? 'Cancelar edicao' : 'Cancelar cadastro'}
                    </button>
                  </div>
                </form>
              )}

              <div className="merchant-products-panel">
                <div className="merchant-products-toolbar">
                  <div>
                    <h2>Produtos cadastrados</h2>
                    <p>
                      {productSummary.total === 0
                        ? 'Cadastre seu primeiro produto para comecar a vender.'
                        : `Exibindo ${filteredProducts.length} de ${productSummary.total} produto(s).`}
                    </p>
                  </div>
                  <div className="merchant-products-summary-grid">
                    <div className="merchant-products-summary-card">
                      <span>Total</span>
                      <strong>{productSummary.total}</strong>
                    </div>
                    <div className="merchant-products-summary-card">
                      <span>Ativos</span>
                      <strong>{productSummary.active}</strong>
                    </div>
                    <div className="merchant-products-summary-card">
                      <span>Promocao</span>
                      <strong>{productSummary.promo}</strong>
                    </div>
                  </div>
                </div>

                <div className="merchant-products-filters">
                  <div className="form-group">
                    <label>Buscar produto</label>
                    <input
                      type="text"
                      value={productSearchTerm}
                      onChange={(event) => setProductSearchTerm(event.target.value)}
                      placeholder="Digite nome ou descricao"
                    />
                  </div>
                  <div className="form-group">
                    <label>Filtro rapido</label>
                    <select
                      value={productListFilter}
                      onChange={(event) => setProductListFilter(event.target.value)}
                    >
                      {PRODUCT_LIST_FILTER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {loadingProducts ? (
                  <div className="merchant-products-state">Carregando produtos...</div>
                ) : filteredProducts.length === 0 ? (
                  <div className="merchant-products-state">
                    {products.length === 0
                      ? 'Nenhum produto cadastrado.'
                      : 'Nenhum produto encontrado para o filtro atual.'}
                  </div>
                ) : (
                  <div className="merchant-products-grid">
                    {filteredProducts.map((product) => {
                      const imageUrl = product.image_url || product.imagem || '';
                      const name = getProductDisplayName(product);
                      const description = getProductDisplayDescription(product);
                      const basePrice = getProductPriceInReais(product);
                      const promotionFinalPrice = getPromotionFinalPriceInReais(product);
                      const hasPromotion = Boolean(product?.promotion?.is_active);
                      const extras = Array.isArray(product?.extras) ? product.extras : [];
                      const isActive = product?.available !== false;
                      const discountPercent = Number(product?.promotion?.discount_percent || 0);

                      return (
                        <article key={product.id} className="merchant-product-card">
                          <div className="merchant-product-card-image-wrap">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={name}
                                className="merchant-product-card-image"
                              />
                            ) : (
                              <div className="merchant-product-card-image-placeholder">
                                <Package size={20} />
                              </div>
                            )}
                            {hasPromotion && discountPercent > 0 ? (
                              <span className="merchant-product-badge promo">{discountPercent}% OFF</span>
                            ) : null}
                            <span className={`merchant-product-badge status ${isActive ? 'active' : 'inactive'}`}>
                              {isActive ? 'Ativo' : 'Inativo'}
                            </span>
                          </div>

                          <div className="merchant-product-card-body">
                            <h3 title={name}>{name}</h3>
                            <p className="merchant-product-description" title={description || 'Sem descricao'}>
                              {description || 'Sem descricao cadastrada.'}
                            </p>

                            <div className="merchant-product-price-row">
                              <strong>{formatMoney(hasPromotion && promotionFinalPrice != null ? promotionFinalPrice : basePrice)}</strong>
                              {hasPromotion ? (
                                <span className="merchant-product-price-original">{formatMoney(basePrice)}</span>
                              ) : null}
                            </div>

                            <div className="merchant-product-extras-row">
                              {extras.length > 0 ? (
                                <>
                                  <span className="merchant-product-extra-label">Adicionais:</span>
                                  <div className="merchant-product-extras-chips">
                                    {extras.slice(0, 3).map((extra) => (
                                      <span key={`${product.id}-${extra.id}`} className="merchant-product-extra-chip">
                                        {normalizeExtraLabel(extra?.name || extra?.nome || 'Opcional')}
                                      </span>
                                    ))}
                                    {extras.length > 3 ? (
                                      <span className="merchant-product-extra-chip muted">+{extras.length - 3}</span>
                                    ) : null}
                                  </div>
                                </>
                              ) : (
                                <span className="merchant-product-no-extra">Sem adicionais</span>
                              )}
                            </div>

                            <div className="merchant-product-actions">
                              <button type="button" className="product-action-btn edit" onClick={() => handleEditProduct(product)}>
                                <Edit2 size={14} />
                                Editar
                              </button>
                              <button type="button" className="product-action-btn delete" onClick={() => handleDeleteProduct(product.id)}>
                                <Trash2 size={14} />
                                Excluir
                              </button>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {active === 'financeiro' && (
            <div className="merchant-finance-tab">
              <div className="merchant-finance-header">
                <h1>Financeiro</h1>
                <p>Gestao de repasses e acompanhamento financeiro da sua operacao.</p>
              </div>

              <div className="merchant-finance-hero-grid">
                <article className="merchant-finance-hero-card primary">
                  <span>Saldo a receber</span>
                  <strong>{formatMoney(financeSummary.value_to_receive)}</strong>
                  <small>Valor previsto para proximos repasses</small>
                </article>
                <article className="merchant-finance-hero-card">
                  <span>Recebido no periodo</span>
                  <strong>{formatMoney(financeSummary.value_received)}</strong>
                  <small>Total liquidado para a loja</small>
                </article>
                <article className="merchant-finance-hero-card">
                  <span>Divida futura cash</span>
                  <strong>{formatMoney(financeSummary.cash_debt_pending)}</strong>
                  <small>Valores em dinheiro a acertar com o Correfood</small>
                </article>
              </div>

              <div className="merchant-finance-layout">
                <div className="merchant-finance-side-column">
                  <div className="merchant-finance-account-card">
                    <h3>Conta para repasse</h3>
                    <p>Cadastre PIX ou conta bancaria para receber os repasses do Correfood.</p>

                    {payoutFieldsLocked ? (
                      <div className="merchant-payout-hidden-card">
                        <h4>Dados de repasse protegidos</h4>
                        <p>
                          Metodo de recebimento e chave/conta ficam ocultos apos o salvamento por seguranca.
                          Para alterar os dados, clique em "Trocar conta de recebimento" e confirme sua senha.
                        </p>
                      </div>
                    ) : (
                      <div className="merchant-finance-form-grid">
                        <div className="form-group">
                          <label>Metodo</label>
                          <select
                            value={payoutForm.method}
                            onChange={(event) => setPayoutForm((prev) => ({ ...prev, method: event.target.value === 'bank' ? 'bank' : 'pix' }))}
                            disabled={payoutSaving}
                          >
                            <option value="pix">PIX</option>
                            <option value="bank">Conta bancaria</option>
                          </select>
                        </div>

                        {payoutForm.method === 'pix' ? (
                          <div className="form-group">
                            <label>Chave PIX</label>
                            <input
                              type="text"
                              placeholder="CPF, e-mail, celular ou chave aleatoria"
                              value={payoutForm.pix_key}
                              onChange={(event) => setPayoutForm((prev) => ({ ...prev, pix_key: event.target.value }))}
                              disabled={payoutSaving}
                            />
                          </div>
                        ) : (
                          <>
                            <div className="form-group">
                              <label>Banco</label>
                              <input
                                type="text"
                                placeholder="Nome do banco"
                                value={payoutForm.bank_name}
                                onChange={(event) => setPayoutForm((prev) => ({ ...prev, bank_name: event.target.value }))}
                                disabled={payoutSaving}
                              />
                            </div>
                            <div className="form-group">
                              <label>Agencia</label>
                              <input
                                type="text"
                                placeholder="Agencia"
                                value={payoutForm.bank_agency}
                                onChange={(event) => setPayoutForm((prev) => ({ ...prev, bank_agency: event.target.value }))}
                                disabled={payoutSaving}
                              />
                            </div>
                            <div className="form-group">
                              <label>Conta</label>
                              <input
                                type="text"
                                placeholder="Numero da conta"
                                value={payoutForm.bank_account}
                                onChange={(event) => setPayoutForm((prev) => ({ ...prev, bank_account: event.target.value }))}
                                disabled={payoutSaving}
                              />
                            </div>
                            <div className="form-group">
                              <label>Tipo da conta</label>
                              <input
                                type="text"
                                placeholder="Corrente, poupanca, etc"
                                value={payoutForm.bank_account_type}
                                onChange={(event) => setPayoutForm((prev) => ({ ...prev, bank_account_type: event.target.value }))}
                                disabled={payoutSaving}
                              />
                            </div>
                            <div className="form-group">
                              <label>Titular</label>
                              <input
                                type="text"
                                placeholder="Nome do titular"
                                value={payoutForm.bank_holder_name}
                                onChange={(event) => setPayoutForm((prev) => ({ ...prev, bank_holder_name: event.target.value }))}
                                disabled={payoutSaving}
                              />
                            </div>
                            <div className="form-group">
                              <label>Documento do titular</label>
                              <input
                                type="text"
                                placeholder="CPF ou CNPJ"
                                value={payoutForm.bank_holder_document}
                                onChange={(event) => setPayoutForm((prev) => ({ ...prev, bank_holder_document: event.target.value }))}
                                disabled={payoutSaving}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div className="merchant-payout-actions">
                      <button className={`btn-primary ${payoutFieldsLocked ? 'is-saved' : ''}`} onClick={handleSavePayoutAccount} disabled={payoutSaving || payoutFieldsLocked}>
                        {payoutSaving ? 'Salvando...' : payoutFieldsLocked ? 'Salvo' : 'Salvar dados de repasse'}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary merchant-payout-change-btn"
                        onClick={() => setShowPayoutUnlockCard(true)}
                        disabled={payoutSaving || !hasSavedPayoutData || isPayoutEditing}
                      >
                        Trocar conta de recebimento
                      </button>
                    </div>

                    {payoutFieldsLocked ? (
                      <small className="merchant-payout-lock-hint">
                        Dados salvos no banco da loja. Para alterar, confirme sua senha.
                      </small>
                    ) : null}

                    {showPayoutUnlockCard ? (
                      <div className="merchant-payout-unlock-card">
                        <h4>Confirmar identidade</h4>
                        <p>Digite sua senha de acesso do Correfood para liberar a alteracao dos dados bancarios.</p>
                        <div className="form-group">
                          <label>Senha da conta</label>
                          <input
                            type="password"
                            value={payoutUnlockPassword}
                            onChange={(event) => setPayoutUnlockPassword(event.target.value)}
                            placeholder="Digite sua senha"
                            disabled={payoutUnlocking}
                          />
                        </div>
                        <div className="merchant-payout-unlock-actions">
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={handleUnlockPayoutEditing}
                            disabled={payoutUnlocking}
                          >
                            {payoutUnlocking ? 'Validando...' : 'Confirmar senha e liberar'}
                          </button>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => {
                              if (payoutUnlocking) return;
                              setShowPayoutUnlockCard(false);
                              setPayoutUnlockPassword('');
                            }}
                            disabled={payoutUnlocking}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="merchant-finance-account-card merchant-cash-acceptance-card">
                    <h3>Pagamento em dinheiro</h3>
                    <p>Defina se clientes podem pagar em dinheiro na sua loja.</p>

                    <div className="merchant-voucher-status-row">
                      <span className={`merchant-voucher-pill ${acceptsCashFromCustomer ? 'ok' : 'danger'}`}>
                        {acceptsCashFromCustomer ? 'Dinheiro habilitado' : 'Dinheiro desabilitado'}
                      </span>
                      <span className={`merchant-voucher-pill ${cashSettingLocked ? 'ok' : 'pending'}`}>
                        {cashSettingLocked ? 'Configuracao salva' : 'Edicao liberada'}
                      </span>
                    </div>

                    {cashSettingLocked ? (
                      <div className="merchant-cash-setting-hidden-card">
                        <h4>Configuracao bloqueada</h4>
                        <p>
                          A configuracao foi salva com sucesso. Clique em "Trocar opcao" para editar novamente.
                        </p>
                      </div>
                    ) : null}

                    <div className="merchant-finance-form-grid">
                      <div className="form-group">
                        <label>Aceitar dinheiro em especie</label>
                        <select
                          value={acceptsCashFromCustomer ? 'enabled' : 'disabled'}
                          onChange={(event) => setAcceptsCashFromCustomer(event.target.value === 'enabled')}
                          disabled={cashPaymentSettingSaving || cashSettingLocked}
                        >
                          <option value="enabled">Sim, aceitar dinheiro</option>
                          <option value="disabled">Nao, ocultar no checkout</option>
                        </select>
                      </div>
                    </div>

                    <div className="merchant-payout-actions">
                      <button
                        type="button"
                        className={`btn-primary ${cashSettingLocked ? 'is-saved' : ''}`}
                        onClick={handleSaveCashPaymentSetting}
                        disabled={cashPaymentSettingSaving || cashSettingLocked || !hasCashSettingChanged}
                      >
                        {cashPaymentSettingSaving ? 'Salvando...' : cashSettingLocked ? 'Salvo' : 'Salvar configuracao de dinheiro'}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary merchant-payout-change-btn"
                        onClick={handleUnlockCashPaymentSetting}
                        disabled={cashPaymentSettingSaving || isCashPaymentEditing}
                      >
                        Trocar opcao
                      </button>
                    </div>
                    <small className="merchant-finance-scope-note">
                      Quando desativado, a opcao Dinheiro deixa de aparecer para o cliente no checkout desta loja.
                    </small>
                  </div>

                  <div className="merchant-finance-account-card merchant-finance-shortcuts">
                    <h3>Voucher em menu separado</h3>
                    <p>A configuracao de VA/VR foi movida para a aba exclusiva de Voucher para facilitar a operacao.</p>
                    <button type="button" className="btn-secondary merchant-finance-shortcut-btn" onClick={() => setActive('voucher')}>
                      Ir para menu Voucher
                    </button>
                  </div>
                </div>

                <div className="merchant-finance-overview">
                  <div className="merchant-finance-filters-card">
                    <div className="merchant-finance-filter-row">
                      <div className="form-group">
                        <label>Data inicial</label>
                        <input
                          type="date"
                          value={financeFilters.date_from}
                          onChange={(event) => setFinanceFilters((prev) => ({ ...prev, date_from: event.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>Data final</label>
                        <input
                          type="date"
                          value={financeFilters.date_to}
                          onChange={(event) => setFinanceFilters((prev) => ({ ...prev, date_to: event.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>Status do pedido</label>
                        <select
                          value={financeFilters.status}
                          onChange={(event) => setFinanceFilters((prev) => ({ ...prev, status: event.target.value }))}
                        >
                          <option value="all">Todos</option>
                          <option value="pending">Pendentes</option>
                          <option value="completed">Finalizados</option>
                          <option value="cancelled">Cancelados</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Pagamento</label>
                        <select
                          value={financeFilters.payment}
                          onChange={(event) => setFinanceFilters((prev) => ({ ...prev, payment: event.target.value }))}
                        >
                          <option value="all">Todos</option>
                          <option value="cash">Dinheiro</option>
                          <option value="pix">PIX</option>
                          <option value="card">Cartao</option>
                        </select>
                      </div>
                      <div className="form-group search">
                        <label>Pedido</label>
                        <input
                          type="text"
                          placeholder="Buscar por numero do pedido"
                          value={financeFilters.q}
                          onChange={(event) => setFinanceFilters((prev) => ({ ...prev, q: event.target.value }))}
                        />
                      </div>
                      <div className="merchant-finance-actions">
                        <button className="btn-secondary" onClick={handleApplyFinanceFilters} disabled={financeLoading}>
                          {financeLoading ? 'Atualizando...' : 'Aplicar filtros'}
                        </button>
                      </div>
                    </div>
                    <small className="merchant-finance-scope-note">
                      O menu Financeiro mostra repasses da operacao geral. Para VA/VR, use o menu Voucher.
                    </small>
                  </div>

                  {financeError ? <p className="merchant-finance-error">{financeError}</p> : null}

                  <div className="merchant-finance-cards">
                    <div className="merchant-finance-card"><span>Pedidos no resultado</span><strong>{nonVoucherFinanceOrders.length}</strong></div>
                    <div className="merchant-finance-card"><span>Valor a receber</span><strong>{formatMoney(financeSummary.value_to_receive)}</strong></div>
                    <div className="merchant-finance-card"><span>Valor recebido</span><strong>{formatMoney(financeSummary.value_received)}</strong></div>
                    <div className="merchant-finance-card"><span>Vendas do dia</span><strong>{formatMoney(financeSummary.sales_day)}</strong></div>
                    <div className="merchant-finance-card"><span>Vendas do mes</span><strong>{formatMoney(financeSummary.sales_month)}</strong></div>
                    <div className="merchant-finance-card"><span>Vendas do ano</span><strong>{formatMoney(financeSummary.sales_year)}</strong></div>
                    <div className="merchant-finance-card"><span>Ticket medio</span><strong>{formatMoney(financeSummary.ticket_avg)}</strong></div>
                    <div className="merchant-finance-card"><span>Pedidos cancelados</span><strong>{Number(financeSummary.orders_cancelled || 0)}</strong></div>
                    <div className="merchant-finance-card"><span>Divida futura cash</span><strong>{formatMoney(financeSummary.cash_debt_pending)}</strong></div>
                  </div>

                  <div className="merchant-finance-report-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => handleDownloadReport('weekly')}
                      disabled={reportLoading === 'weekly'}
                    >
                      <Download size={16} />
                      {reportLoading === 'weekly' ? 'Gerando semanal...' : 'Relatorio semanal'}
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => handleDownloadReport('monthly')}
                      disabled={reportLoading === 'monthly'}
                    >
                      <Download size={16} />
                      {reportLoading === 'monthly' ? 'Gerando mensal...' : 'Relatorio mensal'}
                    </button>
                  </div>

                  <div className="merchant-finance-table-wrap">
                    <table className="merchant-finance-table">
                      <thead>
                        <tr>
                          <th>Pedido</th>
                          <th>Status</th>
                          <th>Pagamento</th>
                          <th>Entrega</th>
                          <th>Total loja</th>
                          <th>Taxa admin</th>
                          <th>Repasse</th>
                          <th>Status repasse</th>
                          <th>Criado em</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nonVoucherFinanceOrders.map((order) => (
                          <tr key={order.id}>
                            <td>{order.order_number || '-'}</td>
                            <td>{order.status || '-'}</td>
                            <td>{formatPaymentMethodLabel(order.payment_method)}</td>
                            <td>{order.delivery_assignment_label || '-'}</td>
                            <td>{formatMoney(order.merchant_total)}</td>
                            <td>{formatMoney(order.admin_fee)}</td>
                            <td>{formatMoney(order.payout_amount)}</td>
                            <td>{order.payout_status || '-'}</td>
                            <td>{formatDateTime(order.created_at)}</td>
                          </tr>
                        ))}
                        {!nonVoucherFinanceOrders.length && (
                          <tr>
                            <td colSpan={9} className="empty">Nenhum pedido financeiro encontrado para os filtros atuais.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {active === 'fiscal' && (
            <div className="merchant-fiscal-tab">
              <div className="merchant-finance-header">
                <h1>Configuracao fiscal</h1>
                <p>Cadastre CPF/CNPJ e CNAE da loja para liberar habilitacao de voucher (VA/VR).</p>
                <div className="merchant-fiscal-header-meta">
                  <span className={`merchant-voucher-pill ${taxProfileForm.status === 'approved' ? 'ok' : taxProfileForm.status === 'rejected' ? 'danger' : 'pending'}`}>
                    {formatTaxProfileStatus(taxProfileForm.status)}
                  </span>
                  <span className={`merchant-voucher-pill ${taxProfileForm.is_complete ? 'ok' : 'neutral'}`}>
                    {taxProfileForm.is_complete ? 'Cadastro completo' : 'Cadastro incompleto'}
                  </span>
                </div>
              </div>

              <div className="merchant-fiscal-layout">
                <div className="merchant-finance-account-card merchant-tax-profile-card">
                  <h3>Perfil fiscal da loja</h3>
                  <p>Cadastro obrigatorio para habilitacao de voucher (VA/VR).</p>

                  <div className="merchant-voucher-status-row">
                    <span className={`merchant-voucher-pill ${taxProfileForm.status === 'approved' ? 'ok' : taxProfileForm.status === 'rejected' ? 'danger' : 'pending'}`}>
                      Status fiscal: {formatTaxProfileStatus(taxProfileForm.status)}
                    </span>
                    <span className={`merchant-voucher-pill ${taxProfileForm.is_complete ? 'ok' : 'neutral'}`}>
                      {taxProfileForm.is_complete ? 'Cadastro fiscal completo' : 'Cadastro fiscal incompleto'}
                    </span>
                  </div>

                  {taxProfileForm.rejection_reason ? (
                    <p className="merchant-voucher-note">
                      Motivo da reprovacao fiscal: {taxProfileForm.rejection_reason}
                    </p>
                  ) : null}
                  {taxProfileForm.reviewed_at ? (
                    <p className="merchant-voucher-note">
                      Revisado em: {formatDateTime(taxProfileForm.reviewed_at)}
                    </p>
                  ) : null}
                  {taxProfileError ? <p className="merchant-finance-error">{taxProfileError}</p> : null}

                  <div className="merchant-finance-form-grid">
                    <div className="form-group">
                      <label>Tipo de pessoa</label>
                      <select
                        value={taxProfileForm.person_type}
                        disabled={taxProfileLoading || taxProfileSaving}
                        onChange={(event) => handleTaxProfileChange('person_type', event.target.value)}
                      >
                        <option value="pj">Pessoa juridica (CNPJ)</option>
                        <option value="pf">Pessoa fisica (CPF)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>{taxProfileForm.person_type === 'pf' ? 'CPF' : 'CNPJ'}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder={taxProfileForm.person_type === 'pf' ? 'Somente numeros do CPF' : 'Somente numeros do CNPJ'}
                        value={taxProfileForm.document_number}
                        disabled={taxProfileLoading || taxProfileSaving}
                        onChange={(event) => handleTaxProfileChange('document_number', event.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>Razao social / Nome completo</label>
                      <input
                        type="text"
                        placeholder={taxProfileForm.person_type === 'pf' ? 'Nome completo do titular' : 'Razao social da empresa'}
                        value={taxProfileForm.legal_name}
                        disabled={taxProfileLoading || taxProfileSaving}
                        onChange={(event) => handleTaxProfileChange('legal_name', event.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>Nome fantasia (opcional)</label>
                      <input
                        type="text"
                        placeholder="Nome comercial da loja"
                        value={taxProfileForm.trade_name}
                        disabled={taxProfileLoading || taxProfileSaving}
                        onChange={(event) => handleTaxProfileChange('trade_name', event.target.value)}
                      />
                    </div>

                    {taxProfileForm.person_type === 'pj' ? (
                      <div className="form-group">
                        <label>CNAE principal</label>
                        <input
                          type="text"
                          placeholder="Ex.: 5611-2/01"
                          value={taxProfileForm.cnae_primary}
                          disabled={taxProfileLoading || taxProfileSaving}
                          onChange={(event) => handleTaxProfileChange('cnae_primary', event.target.value)}
                        />
                      </div>
                    ) : null}
                  </div>

                  <button className="btn-primary" onClick={handleSaveTaxProfile} disabled={taxProfileSaving || taxProfileLoading}>
                    {taxProfileSaving ? 'Salvando...' : taxProfileLoading ? 'Carregando...' : 'Salvar perfil fiscal'}
                  </button>
                </div>

                <aside className="merchant-finance-account-card merchant-fiscal-help-card">
                  <h3>Checklist de habilitacao</h3>
                  <p>Use este painel para validar rapidamente o que falta antes de solicitar voucher para a loja.</p>
                  <ul className="merchant-fiscal-checklist">
                    <li className={hasTaxDocumentNumber ? 'done' : 'pending'}>
                      <span className="dot" />
                      Documento fiscal valido ({taxProfileForm.person_type === 'pf' ? 'CPF com 11 digitos' : 'CNPJ com 14 digitos'})
                    </li>
                    <li className={hasTaxLegalName ? 'done' : 'pending'}>
                      <span className="dot" />
                      Razao social ou nome completo preenchido
                    </li>
                    {taxProfileForm.person_type === 'pj' ? (
                      <li className={hasTaxCnae ? 'done' : 'pending'}>
                        <span className="dot" />
                        CNAE principal informado
                      </li>
                    ) : null}
                    <li className={taxProfileForm.status === 'approved' ? 'done' : 'pending'}>
                      <span className="dot" />
                      Revisao do ADM concluida (status aprovado)
                    </li>
                  </ul>
                  {taxProfileReadyForVoucher ? (
                    <button type="button" className="btn-secondary merchant-fiscal-cta-btn" onClick={() => setActive('voucher')}>
                      Ir para configuracao de voucher
                    </button>
                  ) : (
                    <small className="merchant-fiscal-help-note">A loja so fica apta para voucher quando o cadastro fiscal estiver completo e aprovado.</small>
                  )}
                </aside>
              </div>
            </div>
          )}

          {active === 'voucher' && (
            <div className="merchant-voucher-tab">
              <div className="merchant-finance-header">
                <h1>Voucher (VA/VR)</h1>
                <p>Gestao de habilitacao, emissores aceitos, tipos de beneficio e comissoes do trilho voucher.</p>
                <div className="merchant-voucher-header-meta">
                  <span className={`merchant-voucher-pill ${voucherSettingsForm.approval_status === 'approved' ? 'ok' : voucherSettingsForm.approval_status === 'rejected' ? 'danger' : 'pending'}`}>
                    Status voucher: {formatVoucherApprovalStatus(voucherSettingsForm.approval_status)}
                  </span>
                  <span className={`merchant-voucher-pill ${taxProfileReadyForVoucher ? 'ok' : 'pending'}`}>
                    Fiscal: {taxProfileReadyForVoucher ? 'Aprovado' : 'Pendente'}
                  </span>
                </div>
              </div>

              <div className="merchant-voucher-layout">
                <div className="merchant-voucher-side-column">
                  <div className="merchant-finance-account-card merchant-voucher-config-card">
                    <h3>Habilitacao da loja</h3>
                    <p>Configure os dados da loja para receber pedidos com voucher.</p>

                    <div className="merchant-voucher-status-row">
                      <span className={`merchant-voucher-pill ${voucherSettingsForm.approval_status === 'approved' ? 'ok' : voucherSettingsForm.approval_status === 'rejected' ? 'danger' : 'pending'}`}>
                        Status: {formatVoucherApprovalStatus(voucherSettingsForm.approval_status)}
                      </span>
                      <span className={`merchant-voucher-pill ${voucherSettingsForm.enabled_for_checkout ? 'ok' : 'neutral'}`}>
                        {voucherSettingsForm.enabled_for_checkout ? 'Apto para voucher' : 'Nao apto para voucher'}
                      </span>
                    </div>

                    {voucherSettingsForm.rejection_reason ? (
                      <p className="merchant-voucher-note">
                        Motivo da reprovacao: {voucherSettingsForm.rejection_reason}
                      </p>
                    ) : null}
                    {voucherSettingsForm.reviewed_at ? (
                      <p className="merchant-voucher-note">
                        Revisado em: {formatDateTime(voucherSettingsForm.reviewed_at)}
                      </p>
                    ) : null}
                    {!taxProfileReadyForVoucher ? (
                      <p className="merchant-voucher-note merchant-voucher-warning-note">
                        Complete e aprove o perfil fiscal no menu "Configuracao fiscal" para liberar voucher no checkout.
                      </p>
                    ) : null}
                    {!taxProfileReadyForVoucher ? (
                      <div className="merchant-voucher-prereq-actions">
                        <button type="button" className="btn-secondary" onClick={() => setActive('fiscal')}>
                          Abrir configuracao fiscal
                        </button>
                      </div>
                    ) : null}
                    {voucherSettingsError ? <p className="merchant-finance-error">{voucherSettingsError}</p> : null}

                    <div className="merchant-finance-form-grid">
                      <label className="merchant-voucher-checkbox">
                        <input
                          type="checkbox"
                          checked={Boolean(voucherSettingsForm.accepts_voucher)}
                          disabled={voucherSettingsLoading || voucherSettingsSaving}
                          onChange={(event) => handleVoucherSettingChange('accepts_voucher', event.target.checked)}
                        />
                        <span>Aceita voucher</span>
                      </label>

                      <div className="form-group">
                        <label>Emissores aceitos</label>
                        <div className="merchant-voucher-checkbox-grid">
                          {VOUCHER_ISSUER_OPTIONS.map((option) => (
                            <label key={option.value} className="merchant-voucher-checkbox">
                              <input
                                type="checkbox"
                                checked={voucherSettingsForm.accepted_issuers.includes(option.value)}
                                disabled={!voucherSettingsForm.accepts_voucher || voucherSettingsLoading || voucherSettingsSaving}
                                onChange={(event) => handleVoucherListToggle('accepted_issuers', option.value, event.target.checked)}
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Tipos aceitos</label>
                        <div className="merchant-voucher-checkbox-grid">
                          {VOUCHER_TYPE_OPTIONS.map((option) => (
                            <label key={option.value} className="merchant-voucher-checkbox">
                              <input
                                type="checkbox"
                                checked={voucherSettingsForm.accepted_types.includes(option.value)}
                                disabled={!voucherSettingsForm.accepts_voucher || voucherSettingsLoading || voucherSettingsSaving}
                                onChange={(event) => handleVoucherListToggle('accepted_types', option.value, event.target.checked)}
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="form-group">
                        <label>ID da loja no parceiro</label>
                        <input
                          type="text"
                          placeholder="partner_store_id"
                          value={voucherSettingsForm.partner_store_id}
                          disabled={!voucherSettingsForm.accepts_voucher || voucherSettingsLoading || voucherSettingsSaving}
                          onChange={(event) => handleVoucherSettingChange('partner_store_id', event.target.value)}
                        />
                      </div>
                    </div>

                    <button
                      className="btn-primary"
                      onClick={handleSaveVoucherSettings}
                      disabled={
                        voucherSettingsSaving
                        || voucherSettingsLoading
                        || (voucherSettingsForm.accepts_voucher && !taxProfileReadyForVoucher)
                      }
                    >
                      {voucherSettingsSaving ? 'Salvando...' : voucherSettingsLoading ? 'Carregando...' : 'Salvar configuracao de voucher'}
                    </button>
                  </div>
                </div>

                <div className="merchant-voucher-overview">
                  <div className="merchant-voucher-toolbar">
                    <div className="merchant-voucher-filter-grid">
                      <div className="form-group">
                        <label>Data inicial</label>
                        <input
                          type="date"
                          value={financeFilters.date_from}
                          onChange={(event) => setFinanceFilters((prev) => ({ ...prev, date_from: event.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>Data final</label>
                        <input
                          type="date"
                          value={financeFilters.date_to}
                          onChange={(event) => setFinanceFilters((prev) => ({ ...prev, date_to: event.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>Status do pedido</label>
                        <select
                          value={financeFilters.status}
                          onChange={(event) => setFinanceFilters((prev) => ({ ...prev, status: event.target.value }))}
                        >
                          <option value="all">Todos</option>
                          <option value="pending">Pendentes</option>
                          <option value="completed">Finalizados</option>
                          <option value="cancelled">Cancelados</option>
                        </select>
                      </div>
                      <div className="form-group search">
                        <label>Pedido</label>
                        <input
                          type="text"
                          placeholder="Buscar por numero do pedido"
                          value={financeFilters.q}
                          onChange={(event) => setFinanceFilters((prev) => ({ ...prev, q: event.target.value }))}
                        />
                      </div>
                      <div className="merchant-finance-actions merchant-voucher-actions">
                        <button className="btn-secondary merchant-voucher-apply-btn" onClick={handleApplyVoucherFilters} disabled={financeLoading}>
                          {financeLoading ? 'Atualizando...' : 'Aplicar filtros'}
                        </button>
                      </div>
                    </div>
                    <small className="merchant-finance-scope-note">Filtro fixo: apenas pagamentos por voucher.</small>
                  </div>

                  {financeError ? <p className="merchant-finance-error">{financeError}</p> : null}

                  <section className="merchant-voucher-section">
                    <div className="merchant-voucher-section-head">
                      <h3>Resumo Voucher</h3>
                      <p>Visao consolidada de pedidos e comissoes no periodo filtrado.</p>
                    </div>
                    <div className="merchant-voucher-summary-grid">
                      <div className="merchant-finance-card"><span>Pedidos voucher</span><strong>{voucherOrders.length}</strong></div>
                      <div className="merchant-finance-card"><span>Comissao total</span><strong>{formatMoney(voucherCommissionsSummary.commission_amount)}</strong></div>
                      <div className="merchant-finance-card"><span>Comissao pendente</span><strong>{formatMoney(voucherCommissionsSummary.by_status?.pending?.commission_amount)}</strong></div>
                      <div className="merchant-finance-card"><span>Comissao faturada</span><strong>{formatMoney(voucherCommissionsSummary.by_status?.invoiced?.commission_amount)}</strong></div>
                      <div className="merchant-finance-card"><span>Comissao paga</span><strong>{formatMoney(voucherCommissionsSummary.by_status?.paid?.commission_amount)}</strong></div>
                      <div className="merchant-finance-card"><span>Comissao vencida</span><strong>{formatMoney(voucherCommissionsSummary.by_status?.overdue?.commission_amount)}</strong></div>
                    </div>
                  </section>

                  <section className="merchant-voucher-section">
                    <div className="merchant-voucher-section-head">
                      <h3>Pedidos Voucher</h3>
                      <p>Pedidos pagos por voucher com emissor/tipo e status de repasse.</p>
                    </div>
                    <div className="merchant-finance-table-wrap merchant-voucher-orders-table-wrap">
                      <table className="merchant-finance-table merchant-voucher-orders-table">
                        <thead>
                          <tr>
                            <th>Pedido</th>
                            <th>Status</th>
                            <th>Pagamento</th>
                            <th>Voucher (emissor/tipo)</th>
                            <th>Entrega</th>
                            <th>Total loja</th>
                            <th>Taxa admin</th>
                            <th>Repasse</th>
                            <th>Status repasse</th>
                            <th>Criado em</th>
                          </tr>
                        </thead>
                        <tbody>
                          {voucherOrders.map((order) => (
                            <tr key={order.id}>
                              <td>{order.order_number || '-'}</td>
                              <td>{order.status || '-'}</td>
                              <td>{formatPaymentMethodLabel(order.payment_method)}</td>
                              <td>{resolveVoucherDetailsLabel(order)}</td>
                              <td>{order.delivery_assignment_label || '-'}</td>
                              <td>{formatMoney(order.merchant_total)}</td>
                              <td>{formatMoney(order.admin_fee)}</td>
                              <td>{formatMoney(order.payout_amount)}</td>
                              <td>{order.payout_status || '-'}</td>
                              <td>{formatDateTime(order.created_at)}</td>
                            </tr>
                          ))}
                          {!voucherOrders.length && (
                            <tr>
                              <td colSpan={10} className="empty">Nenhum pedido voucher encontrado para os filtros atuais.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <section className="merchant-voucher-section">
                    <div className="merchant-voucher-section-head">
                      <h3>Comissoes do Correfood</h3>
                      <p>Controle de cobranca do lojista para pedidos de voucher aprovados.</p>
                    </div>
                    <div className="merchant-finance-table-wrap merchant-voucher-commissions-table-wrap">
                      <table className="merchant-finance-table merchant-voucher-commissions-table">
                        <thead>
                          <tr>
                            <th>Pedido</th>
                            <th>Status comissao</th>
                            <th>Valor bruto</th>
                            <th>Comissao Correfood</th>
                            <th>Liquido estimado loja</th>
                            <th>Vencimento</th>
                            <th>Atualizado em</th>
                          </tr>
                        </thead>
                        <tbody>
                          {voucherCommissionsRows.map((commission) => (
                            <tr key={commission.id}>
                              <td>{voucherOrderNumberMap[String(commission.order_id || '')] || commission.order_id || '-'}</td>
                              <td>{formatVoucherCommissionStatus(commission.status)}</td>
                              <td>{formatMoney(commission.gross_amount)}</td>
                              <td>{formatMoney(commission.commission_amount)}</td>
                              <td>{formatMoney(commission.merchant_net_amount)}</td>
                              <td>{commission.due_date || '-'}</td>
                              <td>{formatDateTime(commission.updated_at || commission.created_at)}</td>
                            </tr>
                          ))}
                          {!voucherCommissionsRows.length && !voucherCommissionsLoading && (
                            <tr>
                              <td colSpan={7} className="empty">Nenhuma comissao de voucher encontrada.</td>
                            </tr>
                          )}
                          {voucherCommissionsLoading && (
                            <tr>
                              <td colSpan={7} className="empty">Carregando comissoes de voucher...</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          )}

          {active === 'metrica' && (
            <div className="merchant-metrics-tab">
              <div className="merchant-finance-header merchant-metrics-header">
                <h1>Metrica</h1>
                <p>Indicadores de desempenho da sua loja com base nos pedidos.</p>
              </div>

              {financeLoading ? <p>Carregando metricas...</p> : null}
              {financeError ? <p className="merchant-finance-error">{financeError}</p> : null}

              <div className="merchant-finance-cards merchant-metrics-kpi-grid">
                <div className="merchant-finance-card merchant-metrics-kpi-card"><span>Vendas (filtro)</span><strong>{formatMoney(financeSummary.filtered_sales_total)}</strong></div>
                <div className="merchant-finance-card merchant-metrics-kpi-card"><span>Ticket medio (filtro)</span><strong>{formatMoney(financeSummary.filtered_ticket_avg)}</strong></div>
                <div className="merchant-finance-card merchant-metrics-kpi-card"><span>Taxa admin (filtro)</span><strong>{formatMoney(financeSummary.filtered_admin_fee_total)}</strong></div>
                <div className="merchant-finance-card merchant-metrics-kpi-card"><span>A receber (filtro)</span><strong>{formatMoney(financeSummary.filtered_value_to_receive)}</strong></div>
                <div className="merchant-finance-card merchant-metrics-kpi-card"><span>Recebido (filtro)</span><strong>{formatMoney(financeSummary.filtered_value_received)}</strong></div>
                <div className="merchant-finance-card merchant-metrics-kpi-card"><span>Pedidos finalizados</span><strong>{Number(financeSummary.orders_completed || 0)}</strong></div>
              </div>

              <div className="merchant-charts-grid merchant-metrics-charts-grid">
                <div className="merchant-chart-card merchant-metrics-chart-card merchant-metrics-weekday-card">
                  <h3>Vendas por dia da semana</h3>
                  <div className="merchant-chart-list merchant-metrics-weekday-list">
                    {weekdaySales.map((item) => {
                      const revenue = Number(item.revenue || 0);
                      const width = weekdayMaxRevenue > 0 ? Math.max((revenue / weekdayMaxRevenue) * 100, 3) : 0;
                      return (
                        <div className="merchant-chart-row merchant-metrics-weekday-row" key={item.key}>
                          <span className="label">{item.label}</span>
                          <div className="bar-bg">
                            <div className="bar-fill" style={{ width: `${width}%` }} />
                          </div>
                          <span className="value">{formatMoney(revenue)}</span>
                        </div>
                      );
                    })}
                    {!weekdaySales.length && <p className="empty">Sem dados no periodo.</p>}
                  </div>
                </div>

                <div className="merchant-chart-card merchant-metrics-chart-card merchant-metrics-monthday-card">
                  <h3>Vendas por dia do mes</h3>
                  <p className="merchant-metrics-scroll-hint">Arraste para ver todos os dias do periodo.</p>
                  <div className="merchant-month-chart-scroll merchant-metrics-month-scroll">
                    {monthdaySales.map((item) => {
                      const revenue = Number(item.revenue || 0);
                      const height = monthdayMaxRevenue > 0 ? Math.max((revenue / monthdayMaxRevenue) * 100, 2) : 0;
                      return (
                        <div className="merchant-month-bar" key={`day-${item.day}`}>
                          <div className="bar-column">
                            <div className="bar-value" style={{ height: `${height}%` }} />
                          </div>
                          <span className="bar-label">{item.day}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="merchant-chart-card merchant-metrics-chart-card merchant-metrics-top-products-card">
                <h3>Produtos mais vendidos</h3>
                <div className="merchant-top-products merchant-metrics-top-products">
                  {financeTopProducts.map((product, index) => (
                    <div className="merchant-top-product-item merchant-metrics-top-product-item" key={`${product.product_name}-${index}`}>
                      <div>
                        <strong>{product.product_name}</strong>
                        <p>{Number(product.quantity || 0)} pedidos</p>
                      </div>
                      <span>{formatMoney(product.revenue)}</span>
                    </div>
                  ))}
                  {!financeTopProducts.length && <p className="empty">Sem dados de produtos para o periodo.</p>}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
















